<template>
  <div>
    <loading
      :active.sync="isLoading"
      color="#9de35c"
      loader="bars"
      :width="loadingSize"
      :height="loadingSize"
      :is-full-page="loadingFullPage"
    />
    <v-row
      class="devices-toolbar-row mx-0 mb-2"
      align="center"
    >
      <v-col
        cols="auto"
        class="py-1 py-sm-2"
      >
        <v-switch
          v-model="newDevicesSwitch"
          hide-details
          label="Show new devices"
          class="devices-new-switch ma-0 mt-1"
        />
      </v-col>
      <v-col
        class="py-1 py-sm-2 text-right"
      >
        <!-- Desktop / tablet: inline button toolbar -->
        <div class="devices-toolbar-actions hidden-md-and-down">
          <app-btn
            v-for="action in toolbarActions"
            :key="action.key"
            class="devices-toolbar-btn"
            :color="action.color || 'primary'"
            :disabled="action.disabled"
            :loading="action.loading"
            @click="action.handler"
          >
            <v-icon class="mr-1">
              {{ action.icon }}
            </v-icon> {{ action.label }}
          </app-btn>
        </div>

        <!-- Mobile: a single button that expands into the actions menu and
             auto-collapses once an action is chosen. -->
        <div class="devices-toolbar-actions-menu hidden-lg-and-up">
          <v-menu
            v-model="actionsMenuOpen"
            offset-y
            left
            min-width="220"
            content-class="device-actions-menu"
          >
            <template #activator="{ on, attrs }">
              <app-btn
                class="devices-toolbar-btn"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon class="mr-1">
                  mdi-dots-vertical
                </v-icon> Actions
                <v-icon class="ml-1">
                  mdi-menu-down
                </v-icon>
              </app-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="action in toolbarActions"
                :key="action.key"
                :disabled="action.disabled"
                @click="action.handler"
              >
                <v-list-item-icon class="mr-3">
                  <v-icon :color="action.color">{{ action.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ action.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-col>
    </v-row>
    <material-card
      icon="mdi-cellphone-cog"
      icon-small
      color="primary"
      :title="selectedCompanyName + ' Devices'"
    >
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            sm="6"
            md="4"
            class="py-2"
          >
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              class="devices-search-field"
              hide-details
              label="Search records"
              single-line
            />
          </v-col>
        </v-row>
        <v-divider class="mt-1" />
        <div
          v-show="$vuetify.breakpoint.mdAndUp"
          class="devices-table-scroll"
        >
          <v-data-table
            v-model="selectedDevices"
            class="devices-data-table"
            :headers="tableHeaders"
            :items="mobileStatuses"
          :options.sync="options"
          :search.sync="search"
          :server-items-length="totalDevices"
          :loading="loading"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          item-key="id"
          show-select
          :show-expand="isExpand"
          :items-per-page="5"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [5, 10, 15],
          }"
          @item-expanded="(data) => onExpanded(data)"
        >
          <template v-slot:top>
            <v-dialog
              v-model="dialogConfigurations"
              max-width="1350px"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">Add Configurations</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <div
                      class="d-flex align-center justify-center ma-1 mb-1"
                      style="padding: 15px 20px; border-radius: 5px; background: #f5f5f5;"
                    >
                      <v-autocomplete
                        v-model="referenceId"
                        label="Reference"
                        :items="companies"
                        item-text="pathStr"
                        item-value="id"
                        hide-details
                      />
                      <app-btn
                        class="mt-1 ml-5"
                        @click="chooseReference"
                      >
                        Choose
                      </app-btn>
                    </div>
                    <multi-config-form v-model="editedItems" />
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="closeConfigurations"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="addConfigurations"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <app-list
              :dialog="dialogAppList"
              @close="dialogAppList = false"
            />
            <device-files-dialog
              :dialog="deviceFilesDialog"
              :device-id="currentDeviceId"
              @actions="(actions) => (workingActions = actions)"
              @close="deviceFilesDialog = false"
            />
            <device-messages-dialog
              v-model="deviceMessagesDialog"
              :device-id="currentDeviceId"
            />
            <v-dialog
              v-model="dialogEditDeviceName"
              max-width="420px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Edit Vehicle Number
                </v-card-title>
                <v-card-text>
                  <v-alert
                    dense
                    text
                    type="info"
                    class="mb-3"
                  >
                    Put only the vehicle number here. To store extra details like Driver Name or Phone Number, use the Store info column.
                  </v-alert>
                  <v-text-field
                    v-model="editDeviceNameValue"
                    label="Vehicle Number"
                    hint="Use uppercase letters first, followed by numbers only. Example: T7518."
                    persistent-hint
                    :error-messages="editDeviceNameError"
                    autofocus
                    @input="editDeviceNameError = ''"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogEditDeviceName = false"
                  >
                    Cancel
                  </v-btn>
                  <app-btn @click="saveDeviceNameEdit">
                    Save
                  </app-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogEditMacAddress"
              max-width="440px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Change MAC Address
                </v-card-title>
                <v-card-text>
                  <v-alert
                    dense
                    text
                    type="warning"
                    class="mb-3"
                  >
                    Changing the MAC address re-identifies the device. Only do this when the hardware has been replaced.
                  </v-alert>
                  <v-text-field
                    :value="editMacCurrentValue"
                    label="Current MAC Address"
                    readonly
                    disabled
                  />
                  <v-text-field
                    v-model="editMacValue"
                    label="New MAC Address"
                    hint="Example: AA:BB:CC:44:55:66"
                    persistent-hint
                    :error-messages="editMacError"
                    autofocus
                    @input="editMacError = ''"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogEditMacAddress = false"
                  >
                    Cancel
                  </v-btn>
                  <app-btn
                    :loading="editMacSaving"
                    @click="saveMacAddressEdit"
                  >
                    Save
                  </app-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogDeviceAdditionalInfo"
              max-width="720px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Additional Device Info
                </v-card-title>
                <v-card-text>
                  <v-progress-linear
                    v-if="deviceAdditionalInfoLoading"
                    indeterminate
                    color="primary"
                    class="mb-4"
                  />
                  <v-alert
                    v-if="!deviceAdditionalInfoLoading && deviceAdditionalInfoError"
                    dense
                    text
                    type="warning"
                  >
                    {{ deviceAdditionalInfoError }}
                  </v-alert>
                  <div
                    v-if="!deviceAdditionalInfoLoading && deviceAdditionalInfoRows.length"
                    class="device-additional-info-grid"
                  >
                    <div
                      v-for="row in deviceAdditionalInfoRows"
                      :key="row.label"
                      class="device-additional-info-row"
                    >
                      <span class="device-additional-info-row__label">{{ row.label }}</span>
                      <span class="device-additional-info-row__value">{{ row.value }}</span>
                    </div>
                  </div>
                  <v-alert
                    v-if="!deviceAdditionalInfoLoading && !deviceAdditionalInfoError && !deviceAdditionalInfoRows.length"
                    dense
                    text
                    type="info"
                  >
                    No additional info found.
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogDeviceAdditionalInfo = false"
                  >
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogDeviceMeta"
              max-width="640px"
              scrollable
            >
              <v-card>
                <v-card-title class="text-h6 d-block">
                  <div>Store info</div>
                  <div
                    v-if="deviceMetaTargetItem"
                    class="text-subtitle-2 grey--text text--darken-1 mt-1"
                  >
                    {{ deviceMetaTargetItem.name || 'Unnamed device' }}
                  </div>
                </v-card-title>
                <v-card-text>
                  <v-alert
                    v-if="deviceMetaError"
                    dense
                    text
                    type="error"
                    class="mb-3"
                  >
                    {{ deviceMetaError }}
                  </v-alert>
                  <div class="text-caption grey--text text--darken-1 mb-4">
                    Store any label and value pairs for this device. Add as many as you need or remove the ones you don't.
                  </div>

                  <div
                    v-for="(row, index) in deviceMetaRows"
                    :key="index"
                    class="device-meta-row"
                  >
                    <v-text-field
                      v-model.trim="row.label"
                      outlined
                      dense
                      hide-details
                      label="Label"
                      placeholder="e.g. Name"
                      :disabled="deviceMetaSaving"
                      class="device-meta-row__label"
                    />
                    <v-text-field
                      v-model="row.value"
                      outlined
                      dense
                      hide-details
                      label="Value"
                      :disabled="deviceMetaSaving"
                      class="device-meta-row__value"
                    />
                    <v-btn
                      icon
                      small
                      class="device-meta-row__remove"
                      :disabled="deviceMetaSaving"
                      title="Remove field"
                      @click="removeDeviceMetaRow(index)"
                    >
                      <v-icon color="error">
                        mdi-close-circle-outline
                      </v-icon>
                    </v-btn>
                  </div>

                  <v-alert
                    v-if="!deviceMetaRows.length"
                    dense
                    text
                    type="info"
                    class="mb-0"
                  >
                    No fields yet. Use "Add field" to create one.
                  </v-alert>

                  <v-btn
                    text
                    color="primary"
                    class="mt-2 px-2"
                    :disabled="deviceMetaSaving"
                    @click="addDeviceMetaRow"
                  >
                    <v-icon left>
                      mdi-plus
                    </v-icon>
                    Add field
                  </v-btn>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    :disabled="deviceMetaSaving"
                    @click="dialogDeviceMeta = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    :loading="deviceMetaSaving"
                    @click="saveDeviceMetaEdit"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogDeleteLocalData"
              max-width="500px"
            >
              <v-card>
                <v-card-title class="text-h5">
                  Delete Local Records
                </v-card-title>
                <v-card-text align="center">
                  <app-btn
                    class="ma-2"
                    @click="deleteFailedPickingProductsFromLocalDb()"
                  >
                    <v-icon>mdi-delete</v-icon> Delete picking failed Products
                  </app-btn>
                  <app-btn
                    class="mx-2"
                    @click="deleteOrderProductsFromLocalDb()"
                  >
                    <v-icon>mdi-delete</v-icon> Delete order products & Reset
                    Login
                  </app-btn>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="dialogDeleteLocalData = false"
                  >
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogDeleteDevices"
              max-width="520px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Delete Devices From Company
                </v-card-title>
                <v-card-text>
                  <p class="text-body-2 mb-3">
                    Remove {{ selectedDevices.length }} selected device<span v-if="selectedDevices.length !== 1">s</span> from this company?
                  </p>
                  <div
                    v-for="device in selectedDevices"
                    :key="deleteDeviceKey(device)"
                    class="text-body-2 mb-1"
                  >
                    {{ deviceActionDisplayLabel(device) }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogDeleteDevices = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="error"
                    text
                    :loading="deleteDevicesLoading"
                    @click="confirmDeleteDevices"
                  >
                    Delete From Company
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogMessages"
              max-width="500px"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">Messages</span>
                </v-card-title>
                <v-card-text>
                  <v-row class="mx-2 mt-2">
                    <v-col md="10">
                      <v-text-field
                        v-model="message"
                        label="Message"
                      />
                    </v-col>
                    <v-col
                      md="2"
                      class="mt-2"
                    >
                      <v-btn
                        color="success"
                        @click="addMessage"
                      >
                        {{ btn }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-card
                    v-for="message in messages"
                    :key="message.id"
                    color="#E8F5E9"
                    class="ma-1 pa-1"
                  >
                    <v-row class="ma-1">
                      <v-col md="10">
                        {{ message.description }}
                      </v-col>
                      <v-col md="2">
                        <v-icon
                          small
                          color="info"
                          class="mr-2"
                          @click="editMessage(message)"
                        >
                          mdi-pencil
                        </v-icon>
                        <v-icon
                          small
                          color="error"
                          @click="deleteMessage(message)"
                        >
                          mdi-delete
                        </v-icon>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="dialogMessages = false"
                  >
                    Ok
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <device-version-update-dialog
              v-model="dialogRequiredVersion"
              :devices="requiredVersionDialogDevices"
              :company-id="selectedCompanyId"
              :company-name="selectedCompanyName"
              :company-path="selectedCompanyPath"
              @devices-change="syncRequiredVersionDialogDevices"
              @updated="applyRequiredVersionUpdates"
            />
            <force-stop-app-dialog
              v-model="dialogForceStopApp"
              :selected-company-id="selectedCompanyId"
              :devices="forceStopDevices"
            />
            <v-dialog
              v-model="dialogForceUninstall"
              max-width="680px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Force Uninstall App
                </v-card-title>
                <v-card-text>
                  <v-alert
                    dense
                    text
                    type="info"
                    class="mb-4"
                  >
                    Select a registered company app to uninstall, then choose whether
                    to target the selected devices or a whole device group.
                  </v-alert>
                  <command-target-selector
                    :mode="uninstallTarget.mode"
                    :group-id="uninstallTarget.groupId"
                    :groups="deviceGroups"
                    :device-count="selectedDevices.length"
                    :loading="loadingDeviceGroups"
                    :error="uninstallTarget.error"
                    @update:mode="uninstallTarget.mode = $event; uninstallTarget.error = ''"
                    @update:groupId="uninstallTarget.groupId = $event; uninstallTarget.error = ''"
                  />
                  <v-divider class="mb-4" />
                  <v-alert
                    v-if="forceUninstallError"
                    dense
                    text
                    type="warning"
                    class="mb-4"
                  >
                    {{ forceUninstallError }}
                  </v-alert>
                  <v-select
                    v-model="selectedForceUninstallAppId"
                    :items="forceUninstallApps"
                    item-text="displayLabel"
                    item-value="id"
                    label="App to uninstall"
                    :loading="forceUninstallLoadingApps"
                    :disabled="forceUninstallLoadingApps || forceUninstallSubmitting"
                    no-data-text="No registered apps found"
                  />
                  <div
                    v-if="uninstallTarget.mode === 'devices'"
                    class="force-uninstall-devices"
                  >
                    <div
                      v-for="device in selectedDevices"
                      :key="deleteDeviceKey(device)"
                      class="force-uninstall-device"
                    >
                      {{ deviceActionDisplayLabel(device) }}
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    :disabled="forceUninstallSubmitting"
                    @click="dialogForceUninstall = false"
                  >
                    Cancel
                  </v-btn>
                  <app-btn
                    :disabled="!selectedForceUninstallAppId || forceUninstallLoadingApps || forceUninstallSubmitting"
                    :loading="forceUninstallSubmitting"
                    @click="submitForceUninstall"
                  >
                    Uninstall
                  </app-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogFtpProfile"
              max-width="500px"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">FTP Profile</span>
                </v-card-title>
                <v-card-text>
                  <v-container class="mt-3">
                    <v-row>
                      <v-autocomplete
                        v-model="currentFtpProfile"
                        :items="ftpProfiles"
                        item-text="name"
                        item-value="id"
                        label="FTP Profile"
                      />
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="dialogFtpProfile = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="updateFtpProfile"
                  >
                    Update
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogCurrentServer"
              max-width="500px"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">Current Server</span>
                </v-card-title>
                <v-card-text>
                  <v-container class="mt-3">
                    <v-row>
                      <v-autocomplete
                        v-model="currentServer"
                        :items="serverUrls"
                        item-text="value"
                        item-value="id"
                        label="Current Server Url"
                      />
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="dialogCurrentServer = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="updateServerUrl"
                  >
                    Update
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogBulkDeviceUpdate"
              max-width="500px"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">Device Settings</span>
                </v-card-title>
                <v-card-text>
                  <v-container class="mt-3">
                    <v-row>
                      <v-col cols="12">
                        <v-autocomplete
                          v-model="currentRequiredVersion"
                          :items="appVersions"
                          item-text="fileName"
                          item-value="fileName"
                          label="Required Version"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-autocomplete
                          v-model="currentFtpProfile"
                          :items="ftpProfiles"
                          item-text="name"
                          item-value="id"
                          label="FTP Profile"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-autocomplete
                          v-model="currentServer"
                          :items="serverUrls"
                          item-text="value"
                          item-value="id"
                          label="Current Server Url"
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-row>
                          <div class="ml-2 mt-2">
                            Require Download:
                          </div>
                          <v-icon
                            v-if="currentRequiredDownload"
                            color="success"
                            large
                            @click="isRequiredDownload"
                          >
                            mdi-check-circle
                          </v-icon>
                          <v-icon
                            v-else
                            color="error"
                            large
                            @click="isRequiredDownload"
                          >
                            mdi-close-circle
                          </v-icon>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="dialogBulkDeviceUpdate = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="bulkDeviceUpdate"
                  >
                    Update
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogSetKioskPin"
              max-width="420px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Kiosk unlock PIN
                </v-card-title>
                <v-card-text>
                  <p class="text-body-2 mb-3">
                    Six-digit PIN used when unlocking devices from kiosk mode (per company).
                  </p>
                  <v-text-field
                    v-model="kioskPinNew"
                    type="password"
                    maxlength="6"
                    counter="6"
                    label="New PIN"
                    inputmode="numeric"
                    autocomplete="new-password"
                    :rules="[kioskPinRule]"
                    @keyup.enter="saveKioskUnlockPin"
                  />
                  <v-text-field
                    v-model="kioskPinNewConfirm"
                    type="password"
                    maxlength="6"
                    label="Confirm PIN"
                    inputmode="numeric"
                    autocomplete="new-password"
                    :rules="[kioskPinRule, kioskPinMatchRule]"
                    @keyup.enter="saveKioskUnlockPin"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogSetKioskPin = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    :loading="kioskPinSaving"
                    @click="saveKioskUnlockPin"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogLockDevice"
              max-width="440px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Lock device
                </v-card-title>
                <v-card-text>
                  <p class="text-body-2">
                    This sends a lock command to the device. Continue?
                  </p>
                  <p
                    v-if="lockTargetItem"
                    class="text-subtitle-2 font-weight-bold"
                  >
                    {{ deviceActionDisplayLabel(lockTargetItem) }}
                  </p>
                  <v-divider class="my-3" />
                  <command-expiry-picker
                    v-model="lockExpiry"
                    :default-info="expiryInfoFor('Lock')"
                    :disabled="lockSending"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogLockDevice = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    :loading="lockSending"
                    @click="confirmLockDevice"
                  >
                    Lock
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogUnlockDevice"
              max-width="440px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Unlock device
                </v-card-title>
                <v-card-text>
                  <p class="text-body-2">
                    This sends an unlock command to the device. Continue?
                  </p>
                  <p
                    v-if="unlockTargetItem"
                    class="text-subtitle-2 font-weight-bold"
                  >
                    {{ deviceActionDisplayLabel(unlockTargetItem) }}
                  </p>
                  <v-divider class="my-3" />
                  <command-expiry-picker
                    v-model="unlockExpiry"
                    :default-info="expiryInfoFor('Unlock')"
                    :disabled="unlockSending"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogUnlockDevice = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    :loading="unlockSending"
                    @click="confirmUnlockDevice"
                  >
                    Unlock
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogUnlockCodes"
              max-width="1180px"
            >
              <v-card>
                <v-card-title class="text-h6 unlock-dialog-title">
                  <span>Unlock codes</span>
                  <v-chip
                    v-if="unlockCodeSelectionActive"
                    class="ml-3"
                    small
                    color="primary"
                    outlined
                  >
                    Selected devices only
                    <v-btn
                      icon
                      x-small
                      class="ml-1"
                      aria-label="Show all company devices"
                      @click="clearUnlockCodeSelectionFilter"
                    >
                      <v-icon x-small>
                        mdi-close
                      </v-icon>
                    </v-btn>
                  </v-chip>
                  <v-spacer />
                  <v-btn
                    icon
                    small
                    aria-label="Close unlock codes"
                    @click="dialogUnlockCodes = false"
                  >
                    <v-icon>
                      mdi-close
                    </v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-row
                    align="center"
                    class="mb-3"
                  >
                    <v-col
                      cols="12"
                      md="5"
                    >
                      <v-text-field
                        v-model="unlockCodeSearch"
                        append-icon="mdi-magnify"
                        hide-details
                        label="Search devices"
                        single-line
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="7"
                      class="text-md-right"
                    >
                      <v-btn
                        small
                        text
                        :loading="unlockCodesLoading"
                        @click="loadUnlockCodeDevices"
                      >
                        <v-icon left small>
                          mdi-refresh
                        </v-icon>
                        Refresh
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-alert
                    v-if="!selectedCompanyId"
                    dense
                    text
                    type="info"
                  >
                    Select a company to view its devices.
                  </v-alert>

                  <v-progress-linear
                    v-if="unlockCodesLoading"
                    indeterminate
                    color="primary"
                    class="mb-3"
                  />

                  <div
                    v-for="group in unlockCodeCompanyGroups"
                    :key="group.company"
                    class="unlock-company-section"
                  >
                    <div class="unlock-company-section__title">
                      <v-icon
                        small
                        class="mr-1"
                      >
                        mdi-domain
                      </v-icon>
                      {{ group.company }}
                      <v-chip
                        x-small
                        class="ml-2"
                      >
                        {{ group.devices.length }}
                      </v-chip>
                    </div>
                    <div class="unlock-company-section__table">
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th>Device</th>
                            <th>Emergency Code</th>
                            <th>Status</th>
                            <th class="text-right">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="device in group.devices"
                            :key="unlockCodeDeviceKey(device)"
                          >
                            <td>
                              <div class="font-weight-medium">
                                {{ device.deviceModel || device.name || device.macAddress || device.id }}
                              </div>
                              <div class="text-caption grey--text text--darken-1">
                                {{ device.macAddress || device.deviceId || device.id }}
                              </div>
                            </td>
                            <td>
                              <div
                                v-if="device.emergencyCode"
                                class="unlock-code unlock-code--compact"
                              >
                                <span class="unlock-code__value">{{ device.emergencyCode.code }}</span>
                              </div>
                              <span
                                v-else-if="device.emergencyCodeViewedAt"
                                class="text-caption grey--text text--darken-1"
                              >
                                {{ device.emergencyCodeDisplay || 'Viewed previously' }}
                              </span>
                              <span
                                v-else
                                class="text-caption grey--text text--darken-1"
                              >
                                {{ device.emergencyCodeDisplay || 'Not loaded' }}
                              </span>
                            </td>
                            <td>
                              <div
                                v-if="device.emergencyCode"
                                class="unlock-device-status"
                              >
                                <v-chip
                                  x-small
                                  :color="device.emergencyCode.isExpired ? 'error' : 'success'"
                                  dark
                                >
                                  {{ device.emergencyCode.isExpired ? 'Expired' : 'Active' }}
                                </v-chip>
                                <span>v{{ device.emergencyCode.codeVersion || '-' }}</span>
                                <span>Expires {{ formatUnlockDate(device.emergencyCode.expiresAt) }}</span>
                                <span>Synced {{ formatUnlockDate(device.emergencyCode.lastSyncedAt) }}</span>
                              </div>
                              <span
                                v-else-if="device.emergencyCodeViewedAt"
                                class="text-caption"
                              >
                                {{ device.statusDisplay || `Code v${device.emergencyCodeVersionFromAudit || '-'} viewed ${formatUnlockDate(device.emergencyCodeViewedAt)}` }}
                              </span>
                              <span
                                v-else-if="device.primaryUnlockEventId"
                                class="text-caption"
                              >
                                Primary {{ device.primaryUnlockQueued ? 'queued' : 'sent' }}. Event {{ device.primaryUnlockEventId }}
                              </span>
                              <span
                                v-else
                                class="text-caption grey--text text--darken-1"
                              >
                                {{ device.statusDisplay || 'Ready' }}
                              </span>
                            </td>
                            <td class="text-right">
                              <div class="unlock-row-actions">
                                <v-btn
                                  x-small
                                  color="primary"
                                  text
                                  :loading="isUnlockCodeDeviceLoading('primary', device)"
                                  @click="sendPrimaryUnlockFromCodes(device)"
                                >
                                  <v-icon left x-small>
                                    mdi-lock-open-variant
                                  </v-icon>
                                  Primary
                                </v-btn>
                                <v-btn
                                  x-small
                                  color="primary"
                                  text
                                  :loading="isUnlockCodeDeviceLoading('view', device)"
                                  @click="viewEmergencyCode(device)"
                                >
                                  <v-icon left x-small>
                                    mdi-eye
                                  </v-icon>
                                  View
                                </v-btn>
                                <v-btn
                                  x-small
                                  color="warning"
                                  text
                                  :loading="isUnlockCodeDeviceLoading('rotate', device)"
                                  @click="rotateEmergencyCode(device)"
                                >
                                  <v-icon left x-small>
                                    mdi-refresh
                                  </v-icon>
                                  Rotate
                                </v-btn>
                                <v-btn
                                  x-small
                                  text
                                  :loading="isUnlockCodeDeviceLoading('audit', device)"
                                  @click="openUnlockAudit(device)"
                                >
                                  <v-icon left x-small>
                                    mdi-history
                                  </v-icon>
                                  Audit
                                </v-btn>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </div>
                  </div>

                  <v-alert
                    v-if="!unlockCodesLoading && selectedCompanyId && unlockCodeCompanyGroups.length === 0"
                    dense
                    text
                    type="info"
                  >
                    {{ unlockCodeSelectionActive ? 'No unlock-code details found for the selected devices.' : 'No devices found for this company.' }}
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogUnlockCodes = false"
                  >
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogUnlockAudit"
              max-width="900px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Unlock audit history
                </v-card-title>
                <v-card-text>
                  <p
                    v-if="unlockAuditTargetItem || unlockTargetItem"
                    class="text-subtitle-2 font-weight-bold mb-3"
                  >
                    {{ deviceActionDisplayLabel(unlockAuditTargetItem || unlockTargetItem) }}
                  </p>
                  <v-data-table
                    :headers="unlockAuditHeaders"
                    :items="unlockAudit"
                    :loading="unlockAuditLoading"
                    :items-per-page="10"
                    dense
                    class="unlock-audit-table"
                  >
                    <template v-slot:[`item.occurredAt`]="{ item }">
                      {{ formatUnlockDate(item.occurredAt) }}
                    </template>
                    <template v-slot:[`item.requestedByUserName`]="{ item }">
                      {{ item.requestedByUserName || 'System' }}
                    </template>
                    <template v-slot:[`item.metadata`]="{ item }">
                      {{ item.metadata || '-' }}
                    </template>
                  </v-data-table>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogUnlockAudit = false"
                  >
                    Close
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogEnrollDevice"
              max-width="440px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Enroll device
                </v-card-title>
                <v-card-text>
                  <p class="text-body-2">
                    This sends an enroll command to the device. Continue?
                  </p>
                  <p
                    v-if="enrollTargetItem"
                    class="text-subtitle-2 font-weight-bold"
                  >
                    {{ deviceActionDisplayLabel(enrollTargetItem) }}
                  </p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogEnrollDevice = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="success"
                    text
                    :loading="enrollSending"
                    @click="confirmEnrollDevice"
                  >
                    Enroll
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog
              v-model="dialogUnenrollDevice"
              max-width="440px"
            >
              <v-card>
                <v-card-title class="text-h6">
                  Unenroll device
                </v-card-title>
                <v-card-text>
                  <p class="text-body-2">
                    This sends an unenroll command to the device. Continue?
                  </p>
                  <p
                    v-if="unenrollTargetItem"
                    class="text-subtitle-2 font-weight-bold"
                  >
                    {{ deviceActionDisplayLabel(unenrollTargetItem) }}
                  </p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="dialogUnenrollDevice = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="error"
                    text
                    :loading="unenrollSending"
                    @click="confirmUnenrollDevice"
                  >
                    Unenroll
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <form-dialog
              v-model="dialogReboot"
              :default-value="{ seconds: 0 }"
              @save="sendReboot"
            >
              <template v-slot:[`form`]="{ item }">
                <v-col cols="12">
                  <v-text-field
                    v-model="item.seconds"
                    type="number"
                    label="Timer in seconds"
                  />
                  <v-divider class="my-3" />
                  <command-expiry-picker
                    v-model="rebootExpiry"
                    :default-info="expiryInfoFor('Reboot')"
                  />
                </v-col>
              </template>
            </form-dialog>
            <form-dialog
              v-model="dialogTelnetHostsDetails"
              validate
              :default-value="{ hosts: [Object.assign({},defaultTelnet)], expiry: '', active: false}"
              @save="saveTelnetHostsDetails"
            >
              <template v-slot:[`form`]="{ item, options }">
                <div
                  v-if="options.item"
                  style="display: grid;grid-template-columns: 1fr 1fr 1fr 1fr; gap: 0.25rem 1rem;"
                >
                  <div v-if="options.item.name">
                    <span style="font-weight: 600;">Name: </span>{{ options.item.name }}
                  </div>
                  <div v-if="options.item.company">
                    <span style="font-weight: 600;">Company: </span>{{ getCompany(options.item.company) }}
                  </div>
                  <div v-if="options.item.deviceModel">
                    <span style="font-weight: 600;">Model: </span>{{ options.item.deviceModel }}
                  </div>
                  <div v-if="options.item.macAddress">
                    <span style="font-weight: 600;">Device Id: </span>{{ options.item.macAddress }}
                  </div>
                  <div v-if="options.item.os">
                    <span style="font-weight: 600;">OS: </span>{{ options.item.os }}
                  </div>
                  <div v-if="options.item.ipAddress">
                    <span style="font-weight: 600;">IP: </span>{{ options.item.ipAddress }}
                  </div>
                  <div>
                    <v-switch
                      v-model="item.active"
                      color="primary"
                      label="Active"
                      hide-details
                      class="ma-0"
                    />
                  </div>
                </div>
                <v-row
                  v-for="(host, i) in item.hosts"
                  :key="i"
                  style="border-radius: 0.5rem; background: #f5f5f5; margin:  20px -12px -12px -12px !important"
                >
                  <v-col cols="4">
                    <v-text-field
                      v-model="host.serverName"
                      label="Server Name"
                      :rules="[v=> item.hosts.filter(x=> x.serverName === v).length <= 1 || 'Server name must be unique.']"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="host.host"
                      label="Server IP"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="host.port"
                      type="number"
                      label="Server Port"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-select
                      v-model="host.type"
                      label="Type"
                      :items="['Telnet', 'Web']"
                    />
                  </v-col>
                  <v-col
                    v-if="host.type === 'Telnet'"
                    cols="4"
                  >
                    <v-select
                      v-model="host.emulationType"
                      label="Emulation Type"
                      :items="['VT220', 'VT100', 'XTERM']"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-switch
                      v-model="host.active"
                      color="primary"
                      label="Active"
                    />
                  </v-col>

                  <v-col
                    v-if="host.deviceId > 0"
                    cols="12"
                    class="text-right"
                  >
                    <!-- <v-btn
                      fab
                      small
                      elevation="2"
                      height="25"
                      width="25"
                      color="primary"
                      @click="updateTelnetHostsDetails(host, item, options)"
                    >
                      <v-icon>mdi-check</v-icon>
                    </v-btn> -->

                    <v-btn
                      fab
                      small
                      elevation="2"
                      height="25"
                      width="25"
                      color="error"
                      class="ml-3"
                      @click="deleteTelnetHostsDetails(host, item, options)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    class="text-right"
                  >
                    <v-btn
                      fab
                      small
                      elevation="2"
                      height="25"
                      width="25"
                      color="primary"
                      class="mt-5 ml-auto"
                      @click="item.hosts.push(Object.assign({}, defaultTelnet))"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>

                  <v-col cols="12">
                    <v-menu
                      v-model="expiryDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="item.expiry"
                          label="Expiry"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        />
                      </template>
                      <v-date-picker
                        v-model="item.expiry"
                        @input="expiryDateMenu = false"
                      />
                    </v-menu>
                  </v-col>

                  <v-col
                    v-if="options.item && options.item.telnetHosts.length && options.item.telnetHosts[0].id > 0"
                    cols="12"
                  >
                    <div>
                      <scanner-code
                        v-if="options.item && options.item.telnetToken"
                        :data="options.item.telnetToken"
                        :size="1"
                        class="ma-6"
                      />

                      <scanner-code
                        v-if="options.item && options.item.telnetToken"
                        :data="options.item.telnetToken"
                        :size="1"
                        type="azteccode"
                        class="ma-6"
                      />

                      <scanner-code
                        v-if="options.item && options.item.telnetToken"
                        :data="options.item.telnetToken"
                        :size="1"
                        type="pdf417"
                        class="ma-6"
                      />

                      <v-btn
                        class="ma-6"
                        @click="fetchTelnetToken(options.item)"
                      >
                        Fetch Token
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </template>
            </form-dialog>
            <!-- <form-dialog
              v-model="dialogTelnetServerDetails"
              :default-value="{ serverName: '', host: '', port: 23 }"
              @save="updateTelnetServerDetails"
            >
              <template v-slot:[`form`]="{ item, options }">
                <v-col cols="12">
                  <v-text-field
                    v-model="item.serverName"
                    label="Server Name"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="item.host"
                    label="Server IP"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="item.port"
                    type="number"
                    label="Server Port"
                  />
                </v-col>

                <div v-if="options.item && options.item.telnet && options.item.telnet.token">
                  <q-r-code
                    :value="options.item.telnet.token"
                    :size="200"
                  />
                </div>
              </template>
            </form-dialog> -->
            <form-dialog
              v-model="dialogCreateDevice"
              :default-value="{ deviceId: '' }"
              @save="addNewDevice"
            >
              <template v-slot:[`form`]="{ item }">
                <v-col cols="12">
                  <v-text-field
                    v-model="item.deviceId"
                    label="Device Id"
                  />
                </v-col>
              </template>
            </form-dialog>
            <form-dialog
              v-model="dialogXmlCommand"
              :default-value="{ info: '' }"
              @save="sendXmlCommand"
            >
              <template v-slot:[`form`]="{ item }">
                <v-col cols="12">
                  <command-target-selector
                    :mode="xmlTarget.mode"
                    :group-id="xmlTarget.groupId"
                    :groups="deviceGroups"
                    :device-count="selectedDevices.length"
                    :loading="loadingDeviceGroups"
                    :error="xmlTarget.error"
                    @update:mode="xmlTarget.mode = $event; xmlTarget.error = ''"
                    @update:groupId="xmlTarget.groupId = $event; xmlTarget.error = ''"
                  />
                  <v-divider class="mb-4" />
                  <v-textarea
                    v-model="item.info"
                    label="XML Command"
                  />
                </v-col>
              </template>
            </form-dialog>
          </template>
          <template v-slot:[`item.name`]="{ item }">
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <button
                  type="button"
                  class="devices-name-action primary--text"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.name || 'Unnamed device' }}
                </button>
              </template>
              <v-list dense>
                <v-list-item @click="openDeviceNameEdit(item)">
                  <v-list-item-icon class="mr-3">
                    <v-icon>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Edit vehicle number</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <a
              v-if="getTaxiLink(item)"
              :href="getTaxiLink(item)"
              target="_blank"
              rel="noopener noreferrer"
              class="devices-taxi-link"
              title="Open in Mytaxis"
            >
              <v-icon
                small
                color="amber darken-2"
              >
                mdi-taxi
              </v-icon>
            </a>
          </template>
          <template v-slot:[`item.macAddress`]="{ item }">
            <table class="app-version-table">
              <tbody>
                <tr><th>ID</th></tr>
                <tr>
                  <td class="app-version-table__value--default">
                    <v-menu
                      v-if="isSuperUser && item.macAddress"
                      offset-y
                    >
                      <template #activator="{ on, attrs }">
                        <button
                          type="button"
                          class="devices-name-action"
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ item.macAddress }}
                        </button>
                      </template>
                      <v-list dense>
                        <v-list-item @click="openMacAddressEdit(item)">
                          <v-list-item-icon class="mr-3">
                            <v-icon>mdi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Edit MAC address</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <span v-else>{{ item.macAddress }}</span>
                  </td>
                </tr>
                <template v-if="item.serialNumber">
                  <tr><th>Serial</th></tr>
                  <tr><td class="app-version-table__value--default">{{ item.serialNumber }}</td></tr>
                </template>
                <template v-if="item.imei">
                  <tr><th>IMEI</th></tr>
                  <tr><td class="app-version-table__value--default">{{ item.imei }}</td></tr>
                </template>
              </tbody>
            </table>
          </template>
          <template v-slot:[`item.appVersion`]="{ item }">
            <template v-for="info in [getAppVersionInfo(item)]">
              <table
                :key="item.id || item.macAddress"
                class="app-version-table"
              >
                <tbody>
                  <tr><th colspan="2">MyDevice</th></tr>
                  <tr>
                    <td>Installed</td>
                    <td class="app-version-table__value--mydevice">{{ info.mydeviceVersion }}</td>
                  </tr>
                  <tr>
                    <td>Default</td>
                    <td class="app-version-table__value--default">{{ info.mydeviceDefault }}</td>
                  </tr>
                  <template v-if="info.hasMytaxi">
                    <tr><th colspan="2">myTaxi</th></tr>
                    <tr>
                      <td>Installed</td>
                      <td class="app-version-table__value--taxi">{{ info.mytaxiVersion }}</td>
                    </tr>
                    <tr>
                      <td>Default</td>
                      <td class="app-version-table__value--taxi">{{ info.mytaxiDefault }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </template>
          </template>
          <template v-slot:[`item.company`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ getDeviceCompanyName(item) }}
                </div>
              </template>
              <span>{{ getDeviceCompanyName(item, true) }}</span>
            </v-tooltip>
          </template>

          <template v-slot:[`item.invoiceNumber`]="{ item }">
            <v-edit-dialog
              large
              @save="updateDeviceName(item)"
            >
              <u class="primary--text">{{ item.invoiceNumber }}</u>
              <template v-slot:input>
                <v-text-field
                  v-model="item.invoiceNumber"
                  label="Invoice Number"
                />
              </template>
            </v-edit-dialog>
          </template>

          <template v-slot:[`item.statusId`]="{ item }">
            <v-edit-dialog
              large
              @save="updateDeviceName(item)"
            >
              <u class="primary--text">{{ item.name }}</u>
              <template v-slot:input>
                <v-text-field
                  v-model="item.name"
                  label="Invoice Number"
                />
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <div class="devices-status">
              <v-btn
                v-if="isAdminOrSuperUser"
                small
                outlined
                color="primary"
                title="Track SignalR commands, acknowledgements and pending queue"
                @click="openDeviceCommands(item)"
              >
                <v-icon left small>mdi-swap-vertical-bold</v-icon>
                Commands
              </v-btn>
              <span v-else class="grey--text">-</span>
            </div>
          </template>
          <template v-slot:[`item.image`]="{ item }">
            <draggable
              class="list-group"
              tag="div"
              :list="[item.id]"
              group="devices"
              :disabled="$vuetify.breakpoint.smAndDown"
              @change="dragEvent($event)"
            >
              <device-icon :device-model="item.deviceModel" />
            </draggable>
          </template>
          <template v-slot:[`item.network`]="{ item }">
            <div class="devices-network">
              <div class="devices-network__grid">
                <div class="devices-network__cell">
                  <span class="devices-network__k">OS</span>
                  <span
                    class="devices-network__v"
                    :title="networkTooltipTitle(item.os)"
                  >{{ formatNetworkField(item.os) }}</span>
                </div>
                <div class="devices-network__cell">
                  <span class="devices-network__k">IP</span>
                  <span
                    class="devices-network__v"
                    :title="networkTooltipTitle(item.ipAddress)"
                  >{{ formatNetworkField(item.ipAddress) }}</span>
                </div>
                <div class="devices-network__cell">
                  <span class="devices-network__k">Signal</span>
                  <span
                    class="devices-network__v"
                    :title="networkTooltipTitle(item.signalStrength)"
                  >{{ formatNetworkField(item.signalStrength) }}</span>
                </div>
                <div class="devices-network__cell">
                  <span class="devices-network__k">AP</span>
                  <span
                    class="devices-network__v"
                    :title="networkTooltipTitle(item.accessPointId)"
                  >{{ formatNetworkField(item.accessPointId) }}</span>
                </div>
                <div class="devices-network__cell">
                  <span class="devices-network__k">Speed</span>
                  <span
                    class="devices-network__v"
                    :title="networkTooltipTitle(item.linkSpeed)"
                  >{{ formatNetworkField(item.linkSpeed) }}</span>
                </div>
                <div class="devices-network__cell">
                  <span class="devices-network__k">ESSID</span>
                  <span
                    class="devices-network__v devices-network__v--essid"
                    :title="networkTooltipTitle(item.essid)"
                  >{{ formatNetworkField(item.essid) }}</span>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:[`item.storeAdditionalInfo`]="{ item }">
            <button
              type="button"
              class="devices-info-cell devices-info-cell--button"
              @click="openDeviceMetaEdit(item)"
            >
              <div
                v-if="deviceMetaDisplayRows(item).length"
                class="devices-info-grid"
              >
                <div
                  v-for="row in deviceMetaDisplayRows(item)"
                  :key="row.label"
                  class="devices-info-grid__row"
                >
                  <span class="devices-info-grid__k">{{ row.label }}</span>
                  <span
                    class="devices-info-grid__v"
                    :title="row.value"
                  >{{ row.value }}</span>
                </div>
              </div>
              <span
                v-else
                class="devices-info-empty primary--text"
              >
                Add info
              </span>
            </button>
          </template>
          <template v-slot:[`item.data`]="{ item }">
            <div class="devices-data-col">
              <button
                type="button"
                class="devices-data-link primary--text"
                @click="
                  deviceMessagesDialog = true;
                  currentDeviceId = item.id;
                "
              >
                Messages
              </button>
              <div>
                <v-edit-dialog
                  large
                  @save="updateDeviceName(item)"
                >
                  <button
                    type="button"
                    class="devices-data-link primary--text"
                  >
                    Notes
                  </button>
                  <template v-slot:input>
                    <v-textarea
                      v-model="item.notes"
                      label="Notes"
                    />
                  </template>
                </v-edit-dialog>
              </div>
              <button
                type="button"
                class="devices-data-link primary--text"
                @click="
                  deviceFilesDialog = true;
                  currentDeviceId = item.id;
                "
              >
                Files
              </button>
              <button
                type="button"
                class="devices-data-link primary--text"
                @click="() => $router.push({ name: 'Remote Assistance', params: { id: item.macAddress } })"
              >
                Remote assistance
              </button>
            </div>
          </template>
          <!-- <template v-slot:[`item.messages`]="{ item }">
            <v-chip
              class="ma-2"
              text-color="white"
              color="success"
              @click="openMessageDialog(item.id, item.messages)"
            >
              {{ item.messages.length }}
            </v-chip>
          </template> -->
          <template v-slot:[`item.deleteLocalData`]="{ item }">
            <v-icon
              color="error"
              @click="openDialogDeleteLocalData(item)"
            >
              mdi-delete
            </v-icon>
          </template>
          <template v-slot:[`item.requiredVersion`]="{ item }">
            <v-chip
              color="success"
              dark
              @click="openDialogRequiredVersion(item)"
            >
              {{ item.requiredVersion }}
            </v-chip>
          </template>
          <template v-slot:[`item.requireDownload`]="{ item }">
            <v-icon
              v-if="item.requireDownload"
              color="success"
              large
              @click="updateRequireDownload(item, false)"
            >
              mdi-check-circle
            </v-icon>
            <v-icon
              v-else
              color="error"
              large
              @click="updateRequireDownload(item, true)"
            >
              mdi-close-circle
            </v-icon>
          </template>
          <template v-slot:[`item.ftpConfiguration`]="{ item }">
            <v-chip
              color="success"
              dark
              @click="openDialogFtpProfile(item)"
            >
              {{ item.ftpConfiguration ? item.ftpConfiguration.name : "" }}
            </v-chip>
          </template>
          <template v-slot:[`item.currentServer`]="{ item }">
            <v-chip
              color="success"
              dark
              @click="openDialogCurrentServer(item)"
            >
              {{ item.currentServer ? item.currentServer.name : "" }}
            </v-chip>
          </template>
          <template v-slot:[`item.lastConnected`]="{ item }">
            <div>{{ timeFormat(item.lastConnected) }}</div>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="devices-row-actions">
              <div class="devices-row-actions__pair">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.enroll', label: 'Enroll Device' }"
                      class="devices-action-btn"
                      color="success"
                      aria-label="Enroll device"
                      v-bind="attrs"
                      v-on="on"
                      @click="openEnrollDialog(item)"
                    >
                      <v-icon dense>
                        mdi-account-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Enroll device</span>
                </v-tooltip>
                <v-tooltip
                  v-if="isSuperUser"
                  bottom
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.unenroll', label: 'Unenroll Device' }"
                      class="devices-action-btn"
                      color="error"
                      aria-label="Unenroll device"
                      v-bind="attrs"
                      v-on="on"
                      @click="openUnenrollDialog(item)"
                    >
                      <v-icon dense>
                        mdi-account-minus
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Unenroll device</span>
                </v-tooltip>
              </div>
              <div class="devices-row-actions__pair">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.lock', label: 'Lock Device' }"
                      class="devices-action-btn"
                      color="primary"
                      aria-label="Lock device"
                      v-bind="attrs"
                      v-on="on"
                      @click="openLockDialog(item)"
                    >
                      <v-icon dense>
                        mdi-lock
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Lock device</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.unlock', label: 'Unlock Device' }"
                      class="devices-action-btn"
                      color="primary"
                      aria-label="Unlock device"
                      v-bind="attrs"
                      v-on="on"
                      @click="openUnlockDialog(item)"
                    >
                      <v-icon dense>
                        mdi-lock-open-variant
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Unlock device</span>
                </v-tooltip>
              </div>
              <div class="devices-row-actions__pair">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.telnetHosts', label: 'Telnet Hosts' }"
                      class="devices-action-btn"
                      :color="item.active ? 'success' : 'error'"
                      aria-label="Telnet hosts"
                      v-bind="attrs"
                      v-on="on"
                      @click="showTelnetHostsDetails(item)"
                    >
                      <v-icon dense>
                        mdi-console
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Telnet hosts</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.reboot', label: 'Reboot Device' }"
                      class="devices-action-btn"
                      color="info"
                      aria-label="Reboot device"
                      v-bind="attrs"
                      v-on="on"
                      @click="openRebootDialog(item)"
                    >
                      <v-icon dense>
                        mdi-restart
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Reboot device</span>
                </v-tooltip>
              </div>
              <div class="devices-row-actions__pair">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.sync', label: 'Sync Device' }"
                      class="devices-action-btn"
                      color="info"
                      aria-label="Sync device"
                      :loading="isDeviceSyncLoading(item)"
                      v-bind="attrs"
                      v-on="on"
                      @click="triggerDeviceSync(item)"
                    >
                      <v-icon dense>
                        mdi-sync
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Sync device</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-feature-key="{ key: 'devices.actions.forceStopApp', label: 'Force Stop App' }"
                      class="devices-action-btn"
                      color="warning"
                      aria-label="Force stop app"
                      v-bind="attrs"
                      v-on="on"
                      @click="openDialogForceStopApp(item)"
                    >
                      <v-icon dense>
                        mdi-stop-circle-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Force stop app</span>
                </v-tooltip>
              </div>
            </div>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td
              :colspan="headers.length"
              class="pa-4"
            >
              <device-detail-tabs
                :current-mobile-status="currentMobileStatus"
                :device="item"
                :reference-id="item.id"
              />
            </td>
          </template>
          </v-data-table>
        </div>

        <!-- Mobile: purpose-built device cards (full control over layout). -->
        <div
          v-if="$vuetify.breakpoint.smAndDown"
          class="device-cards"
        >
            <div
              v-for="item in mobileStatuses"
              :key="item.id"
              class="device-card"
            >
              <div class="device-card__top">
                <v-checkbox
                  :input-value="isDeviceSelected(item)"
                  hide-details
                  dense
                  class="ma-0 pa-0 device-card__check"
                  @change="toggleDeviceSelected(item)"
                />
                <device-icon
                  :device-model="item.deviceModel"
                  class="device-card__icon"
                />
                <div class="device-card__heading">
                  <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                      <button
                        type="button"
                        class="device-card__name devices-name-action primary--text"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ item.name || 'Unnamed device' }}
                      </button>
                    </template>
                    <v-list dense>
                      <v-list-item @click="openDeviceNameEdit(item)">
                        <v-list-item-icon class="mr-3">
                          <v-icon>mdi-pencil</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Edit vehicle number</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <div class="device-card__model">
                    {{ item.deviceModel }}
                  </div>
                </div>
                <v-btn
                  icon
                  small
                  class="device-card__expand"
                  @click="toggleDeviceExpanded(item)"
                >
                  <v-icon>{{ isDeviceExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </div>

              <div class="device-card__actions">
                <v-btn icon small v-feature-key="{ key: 'devices.actions.enroll', label: 'Enroll Device' }" color="success" title="Enroll device" @click="openEnrollDialog(item)">
                  <v-icon dense>
                    mdi-account-plus
                  </v-icon>
                </v-btn>
                <v-btn v-if="isSuperUser" icon small v-feature-key="{ key: 'devices.actions.unenroll', label: 'Unenroll Device' }" color="error" title="Unenroll device" @click="openUnenrollDialog(item)">
                  <v-icon dense>
                    mdi-account-minus
                  </v-icon>
                </v-btn>
                <v-btn icon small v-feature-key="{ key: 'devices.actions.lock', label: 'Lock Device' }" color="primary" title="Lock device" @click="openLockDialog(item)">
                  <v-icon dense>
                    mdi-lock
                  </v-icon>
                </v-btn>
                <v-btn icon small v-feature-key="{ key: 'devices.actions.unlock', label: 'Unlock Device' }" color="primary" title="Unlock device" @click="openUnlockDialog(item)">
                  <v-icon dense>
                    mdi-lock-open-variant
                  </v-icon>
                </v-btn>
                <v-btn icon small v-feature-key="{ key: 'devices.actions.telnetHosts', label: 'Telnet Hosts' }" :color="item.active ? 'success' : 'error'" title="Telnet hosts" @click="showTelnetHostsDetails(item)">
                  <v-icon dense>
                    mdi-console
                  </v-icon>
                </v-btn>
                <v-btn icon small v-feature-key="{ key: 'devices.actions.reboot', label: 'Reboot Device' }" color="info" title="Reboot device" @click="openRebootDialog(item)">
                  <v-icon dense>
                    mdi-restart
                  </v-icon>
                </v-btn>
                <v-btn icon small v-feature-key="{ key: 'devices.actions.sync', label: 'Sync Device' }" color="info" title="Sync device" :loading="isDeviceSyncLoading(item)" @click="triggerDeviceSync(item)">
                  <v-icon dense>
                    mdi-sync
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  v-feature-key="{ key: 'devices.actions.forceStopApp', label: 'Force Stop App' }"
                  color="warning"
                  title="Force stop app"
                  @click="openDialogForceStopApp(item)"
                >
                  <v-icon dense>
                    mdi-stop-circle-outline
                  </v-icon>
                </v-btn>
              </div>

              <div class="device-card__meta">
                <div class="device-card__meta-row">
                  <span class="device-card__k">Last Connected</span>
                  <span class="device-card__v">{{ timeFormat(item.lastConnected) }}</span>
                </div>
                <div class="device-card__meta-row">
                  <span class="device-card__k">Company</span>
                  <span class="device-card__v">{{ getDeviceCompanyName(item) }}</span>
                </div>
                <div class="device-card__meta-row">
                  <span class="device-card__k">Device Id</span>
                  <v-menu
                    v-if="isSuperUser && item.macAddress"
                    offset-y
                  >
                    <template #activator="{ on, attrs }">
                      <button
                        type="button"
                        class="device-card__v devices-name-action primary--text"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ item.macAddress }}
                      </button>
                    </template>
                    <v-list dense>
                      <v-list-item @click="openMacAddressEdit(item)">
                        <v-list-item-icon class="mr-3">
                          <v-icon>mdi-pencil</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Edit MAC address</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <span
                    v-else
                    class="device-card__v"
                  >{{ item.macAddress }}</span>
                </div>
                <div
                  v-if="item.serialNumber"
                  class="device-card__meta-row"
                >
                  <span class="device-card__k">Serial</span>
                  <span class="device-card__v">{{ item.serialNumber }}</span>
                </div>
                <div
                  v-if="item.imei"
                  class="device-card__meta-row"
                >
                  <span class="device-card__k">IMEI</span>
                  <span class="device-card__v">{{ item.imei }}</span>
                </div>
                <div class="device-card__meta-row">
                  <span class="device-card__k">OS</span>
                  <span class="device-card__v">{{ item.os }}</span>
                </div>
                <div class="device-card__meta-row device-card__meta-row--stacked">
                  <span class="device-card__k">App Version</span>
                  <template v-for="info in [getAppVersionInfo(item)]">
                    <table
                      :key="item.id || item.macAddress"
                      class="app-version-table"
                    >
                      <tbody>
                        <tr><th colspan="2">MyDevice</th></tr>
                        <tr>
                          <td>Installed</td>
                          <td class="app-version-table__value--mydevice">{{ info.mydeviceVersion }}</td>
                        </tr>
                        <tr>
                          <td>Default</td>
                          <td class="app-version-table__value--default">{{ info.mydeviceDefault }}</td>
                        </tr>
                        <template v-if="info.hasMytaxi">
                          <tr><th colspan="2">myTaxi</th></tr>
                          <tr>
                            <td>Installed</td>
                            <td class="app-version-table__value--taxi">{{ info.mytaxiVersion }}</td>
                          </tr>
                          <tr>
                            <td>Default</td>
                            <td class="app-version-table__value--taxi">{{ info.mytaxiDefault }}</td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </template>
                </div>
                <div class="device-card__meta-row device-card__meta-row--stacked">
                  <span class="device-card__k">Info</span>
                  <button
                    type="button"
                    class="devices-info-cell devices-info-cell--button device-card__info-button"
                    @click="openDeviceMetaEdit(item)"
                  >
                    <div
                      v-if="deviceMetaDisplayRows(item).length"
                      class="devices-info-grid"
                    >
                      <div
                        v-for="row in deviceMetaDisplayRows(item)"
                        :key="row.label"
                        class="devices-info-grid__row"
                      >
                        <span class="devices-info-grid__k">{{ row.label }}</span>
                        <span
                          class="devices-info-grid__v"
                          :title="row.value"
                        >{{ row.value }}</span>
                      </div>
                    </div>
                    <span
                      v-else
                      class="devices-info-empty primary--text"
                    >
                      Add info
                    </span>
                  </button>
                </div>
              </div>

              <div
                v-if="isDeviceExpanded(item)"
                class="device-card__expanded"
              >
                <device-detail-tabs
                  :current-mobile-status="currentMobileStatus"
                  :device="item"
                  :reference-id="item.id"
                />
              </div>
            </div>

            <div
              v-if="!mobileStatuses.length"
              class="device-cards__empty"
            >
              No devices to show.
            </div>

            <div
              v-if="mobileStatuses.length"
              class="device-cards__pager"
            >
              <v-btn
                icon
                :disabled="options.page <= 1"
                @click="options.page = Math.max(1, options.page - 1)"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="device-cards__page-label">Page {{ options.page }}</span>
              <v-btn
                icon
                :disabled="options.page * options.itemsPerPage >= totalDevices"
                @click="options.page = options.page + 1"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
        </div>
      </v-card-text>
    </material-card>
  </div>
</template>
<script>
  import { get } from 'vuex-pathify'
  import SettingsDataService from '../services/SettingsDataService'
  import Loading from 'vue-loading-overlay'
  import 'vue-loading-overlay/dist/vue-loading.css'
  import DeviceStatuses from './components/DeviceStatuses.vue'
  import DeviceLicenses from './components/DeviceLicenses.vue'
  import DeviceApps from './components/DevicesApps.vue'
  import DeviceLocations from './components/DeviceLocations.vue'
  import DeviceConfigs from './components/DeviceConfigs.vue'
  import DeviceFilesDialog from './components/DeviceFilesDialog.vue'
  import DeviceMessagesDialog from './components/DeviceMessagesDialog.vue'
  import DeviceVersionUpdateDialog from './components/DeviceVersionUpdateDialog.vue'
  import AppList from './components/AppsList.vue'
  import draggable from 'vuedraggable'
  import { parse } from 'json2csv'
  import DeviceIcon from './components/DeviceIcon.vue'
  import DeviceDetailTabs from './components/DeviceDetailTabs.vue'
  import MultiConfigForm from './components/config/MultiConfigForm.vue'
  import RemoteAssistanceDialog from './components/RemoteAssistanceDialog.vue'
  import store from '@/store'
  import WifiConfigs from './WifiConfigs.vue'
  import SignalEvents from './SignalEvents.vue'
  import StatusLogs from './StatusLogs.vue'
  import CommandTargetSelector from './components/CommandTargetSelector.vue'
  import CommandExpiryPicker from './components/CommandExpiryPicker.vue'
  import { buildTtlMinutes } from '@/util/commandCatalog'
  import groupCommandTarget from '@/mixins/groupCommandTarget'
  import ScannerCode from './components/ScannerCode.vue'
  import ForceStopAppDialog from './components/custom/ForceStopAppDialog.vue'

  var moment = require('moment')
  const ANDROID_PACKAGE_FILE_PATTERN = /\.(apk|apkm)(?=$|[?#\s])/i
  // Shorter display-only labels for the Info column so long field names don't force extra width.
  const SHORT_META_LABELS = {
    'phone number': 'Phone',
  }

  export default {
    name: 'Settings',
    components: {
      ScannerCode,
      WifiConfigs,
      RemoteAssistanceDialog,
      MultiConfigForm,
      DeviceIcon,
      DeviceDetailTabs,
      Loading,
      SignalEvents,
      StatusLogs,
      DeviceStatuses,
      CommandTargetSelector,
      CommandExpiryPicker,
      DeviceLicenses,
      DeviceApps,
      DeviceLocations,
      DeviceConfigs,
      DeviceFilesDialog,
      DeviceMessagesDialog,
      DeviceVersionUpdateDialog,
      AppList,
      ForceStopAppDialog,
      draggable,
    },
    mixins: [groupCommandTarget],
    data: () => ({
      lockExpiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
      unlockExpiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
      rebootExpiry: { mode: 'default', ttlMinutes: null, expiresAt: null },
      uninstallTarget: { mode: 'devices', groupId: null, error: '' },
      xmlTarget: { mode: 'devices', groupId: null, error: '' },

      expiryDateMenu: false,
      moment: moment,
      currentMacAddress: '',
      currentRequiredVersion: '',
      currentFtpProfile: '',
      dialogDeleteLocalData: false,
      dialogDeleteDevices: false,
      deleteDevicesLoading: false,
      currentUserName: '',
      dialogFtpProfile: false,
      dialogConfigurations: false,
      referenceId: undefined,
      editedItems: [],
      dialogRequiredVersion: false,
      requiredVersionDialogDevices: [],
      dialogForceStopApp: false,
      forceStopDevices: [],
      dialogForceUninstall: false,
      forceUninstallApps: [],
      selectedForceUninstallAppId: null,
      forceUninstallLoadingApps: false,
      forceUninstallSubmitting: false,
      forceUninstallError: '',
      dialogCurrentServer: false,
      dialogBulkDeviceUpdate: false,
      deviceFilesDialog: false,
      deviceMessagesDialog: false,
      dialogEditDeviceName: false,
      editDeviceNameTargetItem: null,
      editDeviceNameValue: '',
      editDeviceNameError: '',
      dialogEditMacAddress: false,
      editMacTargetItem: null,
      editMacCurrentValue: '',
      editMacValue: '',
      editMacError: '',
      editMacSaving: false,
      dialogDeviceAdditionalInfo: false,
      deviceAdditionalInfoLoading: false,
      deviceAdditionalInfoError: '',
      deviceAdditionalInfo: null,
      dialogDeviceMeta: false,
      deviceMetaTargetItem: null,
      deviceMetaRows: [],
      deviceMetaSaving: false,
      deviceMetaError: '',
      dialogReboot: null,
      dialogCreateDevice: null,
      // dialogTelnetServerDetails: null,
      dialogTelnetHostsDetails: null,
      dialogXmlCommand: null,
      dialogAppList: false,
      sortBy: 'lastConnected',
      locationSortBy: 'time',
      isLoading: false,
      currentDeviceId: 0,
      options: {},
      totalDevices: 0,
      loading: false,
      loadingSize: 128,
      loadingFullPage: false,
      sortDesc: true,
      appSearch: '',
      selectedDevices: [],
      actionsMenuOpen: false,
      allDeviceTableHeaders: [
        {
          text: 'Name',
          sortable: false,
          value: 'name',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          value: 'actions',
          width: '84px',
          class: 'devices-actions-cell',
          cellClass: 'devices-actions-cell',
        },
        { text: 'Last Connected', sortable: false, value: 'lastConnected', width: '124px' },
        {
          text: 'Image',
          sortable: false,
          value: 'image',
          width: '64px',
        },
        { text: 'Company', sortable: false, value: 'company', width: '120px' },
        { text: 'Data', sortable: false, value: 'data', width: '104px' },
        { text: 'Device Id', sortable: false, value: 'macAddress', width: '140px' },
        { text: 'App Version', sortable: false, value: 'appVersion', width: '160px' },
        {
          text: 'Network & OS',
          sortable: false,
          value: 'network',
          width: '212px',
        },
        {
          text: 'Info',
          sortable: false,
          value: 'storeAdditionalInfo',
          width: '212px',
        },
        { text: 'Status', sortable: false, value: 'status', width: '132px' },
        { text: 'Invoice', sortable: false, value: 'invoiceNumber', width: '92px' },
      ],
      isExpand: true,
      expanded: [],
      appVersions: [],
      ftpProfiles: [],
      serverUrls: [],
      singleExpand: true,
      dialogMessages: false,
      btn: '',
      mobileStatuses: [],
      currentMobileStatus: 0,
      messages: [],
      message: '',
      currentMessage: '',
      currentDevice: '',
      currentServer: '',
      currentRequiredDownload: false,
      search: '',
      searchTimer: null,
      newDevicesSwitch: false,
      workingActions: [],
      companyTimer: null,
      deviceCompanyByMac: {},
      // Per company id: { domainUrl, hasMytaxis }, used for the device name taxi link.
      taxiLinkInfoByCompanyId: {},
      defaultTelnet: { serverName: '', host: '', port: 23, type: 'Telnet', emulationType: 'VT220', active: true },
      dialogSetKioskPin: false,
      kioskPinNew: '',
      kioskPinNewConfirm: '',
      kioskPinSaving: false,
      dialogLockDevice: false,
      lockTargetItem: null,
      lockSending: false,
      dialogUnlockDevice: false,
      unlockTargetItem: null,
      unlockSending: false,
      dialogUnlockCodes: false,
      unlockCodesLoading: false,
      unlockCodeDevices: [],
      unlockCodeSelectedKeys: [],
      unlockCodeSearch: '',
      unlockCodeLoadingByDevice: {},
      unlockCodeAuditSummaryByDevice: {},
      unlockAuditTargetItem: null,
      dialogUnlockAudit: false,
      unlockAudit: [],
      unlockAuditLoading: false,
      unlockAuditHeaders: [
        { text: 'Action', value: 'action' },
        { text: 'User', value: 'requestedByUserName' },
        { text: 'Code Version', value: 'emergencyCodeVersion' },
        { text: 'Metadata', value: 'metadata' },
        { text: 'Occurred At', value: 'occurredAt' },
      ],
      dialogEnrollDevice: false,
      enrollTargetItem: null,
      enrollSending: false,
      dialogUnenrollDevice: false,
      unenrollTargetItem: null,
      unenrollSending: false,
      deviceSyncLoadingById: {},
      companySyncSending: false,
    }),
    watch: {
      options: {
        handler () {
          // this.getDataFromApi(this.search);
          this.getDevicesByCompany(this.search)
        },
        deep: true,
      },
      search: {
        handler (val) {
          clearTimeout(this.searchTimer)
          this.searchTimer = setTimeout(() => {
            // this.getDataFromApi(this.search);
            this.getDevicesByCompany(val)
          })
        },
      },

      selectedCompanyId: {
        handler (val) {
          clearTimeout(this.companyTimer)
          this.companyTimer = setTimeout(() => {
            this.getDevicesByCompany(this.search)
          }, 2500)
        },
      },
      newDevicesSwitch (val) {
        if (val) {
          this.getNewDevices()
        } else {
          this.getDevicesByCompany()
        }
      },
      selectedDevices (newVal, oldVal) {
        // On mobile, expand the Actions menu the moment a device is ticked.
        if (!this.$vuetify.breakpoint.smAndDown) {
          return
        }
        const newLen = newVal ? newVal.length : 0
        const oldLen = oldVal ? oldVal.length : 0
        if (newLen > 0 && oldLen === 0) {
          this.actionsMenuOpen = true
        } else if (newLen === 0) {
          this.actionsMenuOpen = false
        }
      },
    },
    computed: {
      ...get('company', ['selectedCompanyId', 'selectedCompanyName', 'selectedCompanyPath']),
      baseConfigCompanyId () {
        return this.$store?.state?.configs?.baseConfigCompanyId || 1
      },
      companies () {
        return store.getters['company/getCompanies']
      },
      isSuperUser () {
        return this.$store.getters['authUser/isSuperUser']
      },
      isAdminOrSuperUser () {
        return this.$store.getters['authUser/isAdminOrSuperUser']
      },
      tableHeaders () {
        if (this.$vuetify.breakpoint.smAndDown) {
          // Mobile card: image as a centered, label-less top row, then the
          // important fields. Only the noisy columns are dropped.
          const order = ['image', 'name', 'actions', 'lastConnected', 'company', 'data', 'macAddress', 'appVersion', 'storeAdditionalInfo']
          return order
            .map((value) => {
              const header = this.allDeviceTableHeaders.find((h) => h.value === value)
              if (!header) {
                return null
              }
              // Blank the "Image" label so the top row is just the device icon.
              return value === 'image' ? { ...header, text: '' } : header
            })
            .filter(Boolean)
        }
        return this.allDeviceTableHeaders
      },
      toolbarActions () {
        const hasSelection = this.selectedDevices && this.selectedDevices.length > 0
        const actions = []

        if (this.workingActions && this.workingActions.length > 0 && hasSelection) {
          actions.push({ key: 'save', label: 'Save changes', icon: 'mdi-view-grid-plus', handler: () => this.saveChanges() })
        }
        actions.push({ key: 'add', label: 'Add New Device', icon: 'mdi-view-grid-plus', handler: () => this.dialogCreateDevice.show(undefined, { formTitle: 'Add New Device' }) })
        actions.push({ key: 'pin', label: 'Set kiosk PIN', icon: 'mdi-form-textbox-password', handler: () => this.openDialogSetKioskPin() })
        actions.push({ key: 'unlock', label: 'Unlock Code', icon: 'mdi-cellphone-key', handler: () => this.openDialogUnlockCodes() })
        // Enabled without a selection: each dialog can target a device group instead.
        actions.push({ key: 'update', label: 'Update Version', icon: 'mdi-update', handler: () => this.openDialogSelectedRequiredVersion() })
        actions.push({ key: 'uninstall', label: 'Uninstall App', icon: 'mdi-delete-alert', color: 'error', handler: () => this.openDialogForceUninstall() })
        actions.push({ key: 'force-stop', label: 'Force Stop App', icon: 'mdi-stop-circle-outline', color: 'warning', handler: () => this.openDialogForceStopApp() })
        actions.push({ key: 'sync-all', label: 'Sync All', icon: 'mdi-sync', disabled: !this.selectedCompanyId || this.companySyncSending, loading: this.companySyncSending, handler: () => this.triggerCompanySync() })
        if (hasSelection) {
          actions.push({ key: 'delete', label: 'Delete From Company', icon: 'mdi-delete', color: 'error', handler: () => this.openDeleteDevicesDialog() })
        }
        actions.push({ key: 'xml', label: 'Send Xml Command', icon: 'mdi-send-variant', handler: () => this.onSendXmlCommandClick() })
        actions.push({ key: 'report', label: 'Generate Report', icon: 'mdi-view-grid-plus', handler: () => this.generateReport() })

        return actions
      },
      deviceAdditionalInfoRows () {
        const info = this.deviceAdditionalInfo || {}
        const ownerName = [info.ownerFirstName, info.ownerMiddleName, info.ownerLastName]
          .filter(value => String(value || '').trim())
          .join(' ')
        const rows = [
          ['Device Name', info.name],
          ['Description', info.description],
          ['MAC Address', info.macAddress],
          ['Active', typeof info.active === 'boolean' ? (info.active ? 'Yes' : 'No') : ''],
          ['Driver', ownerName],
          ['Driver Id', info.ownedDriverId],
          ['Driver No', info.ownedDriverNo || info.username],
          ['Driver Phone', info.ownerMobileNumber || info.ownerPhoneNumber],
          ['Vehicle Id', info.vehicleId],
          ['Vehicle', info.vehicleNo],
          ['Address', info.address],
          ['Suburb', info.suburb],
          ['Latitude', info.latitude],
          ['Longitude', info.longitude],
          ['Previous Latitude', info.previousLatitude],
          ['Previous Longitude', info.previousLongitude],
          ['Previous Address', info.previousAddress],
          ['Previous Location Updated', info.previousLocationUpdatedTime ? this.timeFormat(info.previousLocationUpdatedTime) : ''],
          ['Speed', info.speed ? `${info.speed} km/h` : ''],
          ['Location Updated', info.locationUpdatedTime ? this.timeFormat(info.locationUpdatedTime) : ''],
          ['Last Connected', info.lastConnected ? this.timeFormat(info.lastConnected) : ''],
          ['Device Phone', info.devicePhoneNumber],
          ['Model', info.deviceModel],
          ['OS', info.os],
          ['App Version', info.appVersion],
          ['Tracker', info.trackerMacAddress || info.trackerName || ''],
          ['Tracker Status', info.trackerDisconnected ? 'Disconnected' : ''],
        ]

        return rows
          .filter(([, value]) => String(value || '').trim())
          .map(([label, value]) => ({ label, value }))
      },
      unlockCodeSelectionActive () {
        return this.unlockCodeSelectedKeys.length > 0
      },
      unlockCodeCompanyGroups () {
        const query = String(this.unlockCodeSearch || '').trim().toLowerCase()
        const selectedSet = new Set(this.unlockCodeSelectedKeys)
        const selectionActive = selectedSet.size > 0
        const groups = {}

        this.unlockCodeDevices
          .filter(device => {
            if (selectionActive && !this.unlockCodeMatchesSelection(device, selectedSet)) {
              return false
            }

            if (!query) return true

            return [
              device?.deviceModel,
              device?.name,
              device?.deviceName,
              device?.macAddress,
              device?.deviceMacAddress,
              device?.deviceId,
              device?.username,
              device?.emergencyCodeDisplay,
              device?.statusDisplay,
            ]
              .filter(Boolean)
              .some(value => String(value).toLowerCase().includes(query))
          })
          .forEach(device => {
            const company = this.getDeviceCompanyName(device, true) || this.selectedCompanyName || 'Company'
            if (!groups[company]) {
              groups[company] = []
            }
            groups[company].push(device)
          })

        return Object.keys(groups)
          .sort((a, b) => a.localeCompare(b))
          .map(company => ({
            company,
            devices: groups[company].sort((a, b) => String(a?.name || a?.deviceModel || a?.macAddress || '').localeCompare(String(b?.name || b?.deviceModel || b?.macAddress || ''))),
          }))
      },
    },
    created () {
      // e.g. /softclient/devices?search=TT6324 opens with that device searched.
      const querySearch = String(this.$route.query.search || '').trim()
      if (querySearch) {
        this.search = querySearch
      }

      this.loadUnlockCodeAuditSummaryCache()
      this.initialize()
    },

    methods: {
      onSendXmlCommandClick () {
        this.xmlTarget = { mode: this.selectedDevices.length ? 'devices' : 'group', groupId: null, error: '' }
        this.loadDeviceGroups()
        this.dialogXmlCommand.show(undefined, {
          formTitle: 'Send XML Command',
        })
      },
      formatNetworkField (val) {
        if (val === null || val === undefined) return '—'
        const s = String(val).trim()
        return s.length ? s : '—'
      },
      networkTooltipTitle (val) {
        const s = val != null ? String(val).trim() : ''
        return s || ''
      },
      dragEvent (evt) {
        if (Object.hasOwnProperty.call(evt, 'removed')) {
          this.mobileStatuses.splice(evt.removed.oldIndex, 1)
        }
      },
      onExpanded (data) {
        if (data.value) {
          this.currentMobileStatus = data.item.id
        }
      },
      isDeviceSelected (item) {
        return this.selectedDevices.some((d) => d.id === item.id)
      },
      toggleDeviceSelected (item) {
        if (this.isDeviceSelected(item)) {
          this.selectedDevices = this.selectedDevices.filter((d) => d.id !== item.id)
        } else {
          this.selectedDevices = [...this.selectedDevices, item]
        }
      },
      isDeviceExpanded (item) {
        return this.expanded.some((d) => d.id === item.id)
      },
      toggleDeviceExpanded (item) {
        if (this.isDeviceExpanded(item)) {
          this.expanded = []
          return
        }
        // Single-expand to match the desktop table behaviour.
        this.expanded = [item]
        this.onExpanded({ item, value: true })
      },
      getDataFromApi (search) {
        this.loading = true
        const { sortBy, sortDesc, page, itemsPerPage } = this.options
        var data = {
          page: page,
          limit: itemsPerPage,
        }
        this.$http('Device/GetAllDevices', { params: data })
          .then((response) => {
            this.totalDevices = response.data.total
            this.mobileStatuses = response.data.data
            this.loading = false
          })
          .catch(() => {
            this.mobileStatuses = []
            this.loading = false
          })
      },
      initialize () {
        SettingsDataService.getAppVersions().then((response) => {
          this.appVersions = response.data
        })
        SettingsDataService.getAllFtpConfig().then((response) => {
          this.ftpProfiles = response.data
        })
        SettingsDataService.getServerUrls().then((response) => {
          this.serverUrls = response.data
        })
      },
      getNewDevices () {
        this.loading = true
        const { sortBy, sortDesc, page, itemsPerPage } = this.options
        var data = {
          page: page,
          limit: itemsPerPage,
        }

        this.$http
          .get('Company/GetNewDevices', {
            params: data,
          })
          .then((response) => {
            this.totalDevices = response.data.total
            this.mobileStatuses = response.data.data
            this.loading = false
          })
          .catch((e) => {
            this.totalDevices = 0
            this.mobileStatuses = []
            this.loading = false
          })
      },
      getDevicesByCompany (search) {
        this.loading = true
        const { sortBy, sortDesc, page, itemsPerPage } = this.options
        var data = {
          ...(search && { search }),
          companyId: this.selectedCompanyId,
          page: page,
          limit: itemsPerPage,
        }

        this.$http
          .get('Company/DevicePaginationByCompany', {
            params: data,
          })
          .then((response) => {
            this.totalDevices = 100
            this.mobileStatuses = response.data.map(x => {
              this.rememberDeviceCompany(x)
              x.telnetToken = ''
              x.telnetHosts = []
              return x
            })
            this.loadTaxiLinkInfo(this.mobileStatuses)
            this.loading = false
          })
          .catch((e) => {
            this.totalDevices = 0
            this.mobileStatuses = []
            this.loading = false
          })
      },
      openDeleteDevicesDialog () {
        if (!this.selectedDevices.length) {
          this.$toast.error('Select at least one device.', 'Error', { position: 'topRight' })
          return
        }

        this.dialogDeleteDevices = true
      },
      deleteDeviceKey (device) {
        return device?.id || device?.deviceId || device?.macAddress || ''
      },
      getCompanyDeleteDeviceId (device) {
        return device?.id || device?.deviceId || ''
      },
      async confirmDeleteDevices () {
        if (!this.selectedDevices.length) {
          this.dialogDeleteDevices = false
          return
        }

        const invalidDevice = this.selectedDevices.find(device => !this.getCompanyDeleteDeviceId(device))
        if (invalidDevice) {
          this.$toast.error('One or more selected devices do not have a valid device id.', 'Error', {
            position: 'topRight',
          })
          return
        }

        this.deleteDevicesLoading = true

        try {
          const requests = this.selectedDevices.map((device) => {
            const deviceId = this.getCompanyDeleteDeviceId(device)
            return this.$http.delete(`Company/devices/${encodeURIComponent(deviceId)}/soft-delete`)
          })

          const results = await Promise.allSettled(requests)
          const failed = results.filter(result => result.status === 'rejected')
          const successCount = results.length - failed.length

          if (successCount > 0) {
            this.$toast.success(
              `${successCount} device${successCount === 1 ? '' : 's'} removed from company.`,
              'Success',
              { position: 'topRight' },
            )
          }

          if (failed.length > 0) {
            const firstError = failed[0]?.reason
            this.$toast.error(
              firstError?.response?.data?.message || firstError?.response?.data?.error || 'Some devices could not be removed from the company.',
              'Error',
              { position: 'topRight' },
            )
          }

          this.dialogDeleteDevices = false
          this.selectedDevices = []
          this.getDevicesByCompany(this.search)
        } finally {
          this.deleteDevicesLoading = false
        }
      },
      openMessageDialog (id, messages) {
        this.dialogMessages = true
        this.btn = 'Add'
        this.message = ''
        this.messages = messages
        this.currentDevice = id
      },
      editMessage (message) {
        this.btn = 'Edit'
        this.message = message.description
        this.currentMessage = message.id
      },
      addMessage () {
        if (this.btn === 'Edit') {
          var data = {
            id: this.currentMessage,
            mobileStatusId: this.currentDevice,
            description: this.message,
            active: 1,
          }
          SettingsDataService.update(data).then((response) => {
            var message = this.mobileStatuses.find(
              (x) => x.id === this.currentDevice,
            ).messages
            var index = message.findIndex((x) => x.id === this.currentMessage)
            this.messages.splice(index, 1, data)
          })
          this.btn = 'Add'
        } else {
          const data = {
            mobileStatusId: this.currentDevice,
            description: this.message,
            active: 1,
          }
          SettingsDataService.create(data).then((response) => {
            this.mobileStatuses
              .find((x) => x.id === this.currentDevice)
              .messages.push(response.data)
          })
        }
        this.message = ''
      },
      deleteMessage (message) {
        SettingsDataService.delete(message.id).then((response) => {
          var message = this.mobileStatuses.find(
            (x) => x.id === this.currentDevice,
          ).messages
          var index = message.findIndex((x) => x.id === this.currentMessage)
          this.messages.splice(index, 1)
        })
      },
      getLicence (id) {
        var device = this.mobileStatuses.find((x) => x.id === id)
        if (device) {
          return device.licenses
        }
      },
      timeFormat (time) {
        if (!time) {
          return ''
        }
        // Naive UTC from the API -> the viewer's local zone. This replaced a
        // hardcoded +10h offset, which was only correct in AEST.
        const dateTime = moment.utc(time)
        return dateTime.isValid() ? dateTime.local().format('DD.MM.YYYY h:mm:ss a') : ''
      },
      formatUnlockDate (time) {
        if (!time) {
          return '-'
        }

        const dateTime = moment.utc(time)
        if (!dateTime.isValid()) {
          return '-'
        }

        return dateTime.local().format('DD.MM.YYYY h:mm:ss a')
      },
      openDialogRequiredVersion (item) {
        this.requiredVersionDialogDevices = item ? [item] : []
        this.dialogRequiredVersion = true
      },
      openDialogSelectedRequiredVersion () {
        if (!this.selectedCompanyId) {
          this.$toast.error('Select a company first.', 'Error', {
            position: 'topRight',
          })
          return
        }

        this.requiredVersionDialogDevices = this.selectedDevices.slice()
        this.dialogRequiredVersion = true
      },
      syncRequiredVersionDialogDevices (devices) {
        const items = devices || []
        this.requiredVersionDialogDevices = items.slice()
        this.selectedDevices = items.slice()
      },
      scoreDownloadsConfig (config) {
        const normalizedName = String(config?.name || '')
          .trim()
          .toLowerCase()
        const normalizedPath = String(config?.devicePath || config?.path || '')
          .trim()
          .replace(/\\/g, '/')
          .replace(/^\/+|\/+$/g, '')
          .toLowerCase()
        const lastPathSegment = normalizedPath.split('/').filter(Boolean).pop() || ''

        let score = -1

        if (['download', 'downloads'].includes(lastPathSegment)) {
          score = Math.max(score, 100)
        }

        if (
          normalizedPath === 'download' ||
          normalizedPath === 'downloads' ||
          normalizedPath.endsWith('/download') ||
          normalizedPath.endsWith('/downloads')
        ) {
          score = Math.max(score, 95)
        }

        if (
          ['download', 'downloads', 'script download', 'script downloads', 'scripts download', 'scripts downloads']
            .includes(normalizedName)
        ) {
          score = Math.max(score, 90)
        }

        if (/\bdownloads?\b/.test(normalizedName) && /\bscripts?\b/.test(normalizedName)) {
          score = Math.max(score, 85)
        }

        if (/\bdownloads?\b/.test(normalizedName)) {
          score = Math.max(score, 75)
        }

        if (/\bdownloads?\b/.test(normalizedPath)) {
          score = Math.max(score, 70)
        }

        return score
      },
      getDownloadsSearchCompanyIds () {
        const companyIds = [
          this.selectedCompanyId,
          ...(Array.isArray(this.selectedCompanyPath)
            ? this.selectedCompanyPath.map(company => company?.id).reverse()
            : []),
          this.baseConfigCompanyId,
        ]

        return [...new Set(companyIds.filter(Boolean))]
      },
      async getDeviceConfigsByCompany (companyId) {
        const response = await this.$http.get('DeviceConfig/GetDeviceConfigsByCompany', {
          params: {
            CompanyId: companyId,
          },
        })

        return Array.isArray(response.data) ? response.data : []
      },
      async findDownloadsConfigs () {
        const searchCompanyIds = this.getDownloadsSearchCompanyIds()
        const configResults = await Promise.all(
          searchCompanyIds.map(async (companyId) => ({
            companyId,
            configs: await this.getDeviceConfigsByCompany(companyId),
          })),
        )

        const matches = []

        configResults.forEach(({ companyId, configs }, companyIndex) => {
          configs.forEach((config) => {
            const score = this.scoreDownloadsConfig(config)
            if (score < 0) {
              return
            }

            matches.push({
              companyId,
              companyIndex,
              config,
              score,
            })
          })
        })

        matches.sort((left, right) => {
          if (right.score !== left.score) {
            return right.score - left.score
          }

          if (left.companyIndex !== right.companyIndex) {
            return left.companyIndex - right.companyIndex
          }

          return String(left.config?.name || '').localeCompare(String(right.config?.name || ''))
        })

        return matches
      },
      getNestedStringValue (candidate) {
        if (typeof candidate === 'string') {
          return candidate.trim()
        }

        if (candidate && typeof candidate === 'object') {
          const nestedCandidates = [
            candidate.path,
            candidate.filePath,
            candidate.fileName,
            candidate.name,
            candidate.url,
          ]

          return nestedCandidates.find(value => typeof value === 'string' && value.trim())
        }

        return ''
      },
      normalizeUninstallApkOption (file) {
        const fileName = this.getNestedStringValue(file) || 'Unnamed APK'
        const apkSearchSource = [
          fileName,
          file?.filePath,
          file?.path,
          file?.downloadUrl,
          file?.url,
        ]
          .filter(Boolean)
          .join(' ')

        if (!ANDROID_PACKAGE_FILE_PATTERN.test(apkSearchSource)) {
          return null
        }

        return {
          id: file?.id || file?.deviceConfigFilePathId || fileName,
          name: fileName,
          packageName: file?.packageName || file?.package || '',
          versionName: file?.versionName || file?.version || '',
          versionCode: file?.versionCode || 0,
          uploadedAt: file?.uploadedAt || '',
          isLatestForPackage: !!file?.isLatestForPackage,
          deviceConfigFilePathId: file?.deviceConfigFilePathId || file?.id || 0,
        }
      },
      sortUninstallApkOptions (items) {
        return [...items].sort((left, right) => {
          if (Number(right.isLatestForPackage) !== Number(left.isLatestForPackage)) {
            return Number(right.isLatestForPackage) - Number(left.isLatestForPackage)
          }

          if (Number(right.versionCode || 0) !== Number(left.versionCode || 0)) {
            return Number(right.versionCode || 0) - Number(left.versionCode || 0)
          }

          if (String(right.uploadedAt || '') !== String(left.uploadedAt || '')) {
            return String(right.uploadedAt || '').localeCompare(String(left.uploadedAt || ''))
          }

          return String(left.name || '').localeCompare(String(right.name || ''))
        })
      },
      async loadUninstallApkOptions () {
        const downloadsConfigs = await this.findDownloadsConfigs()

        if (!downloadsConfigs.length) {
          return []
        }

        const files = (
          await Promise.all(
            downloadsConfigs.map(async ({ companyId, config }) => {
              const response = await this.$http.get('DeviceConfig/GetFilePathsByDeviceConfigs', {
                params: {
                  DeviceConfigId: config.id,
                },
              })

              const configFiles = Array.isArray(response.data) ? response.data : []
              return configFiles.map(file => ({
                ...file,
                _downloadsConfigId: config.id,
                _downloadsCompanyId: companyId,
              }))
            }),
          )
        ).flat()

        const seenOptions = new Set()
        return this.sortUninstallApkOptions(
          files
            .map(file => this.normalizeUninstallApkOption(file))
            .filter((option) => {
              if (!option || seenOptions.has(option.id)) {
                return false
              }

              seenOptions.add(option.id)
              return true
            }),
        )
      },
      // `item` set: force stop that single row. Otherwise use the ticked devices.
      openDialogForceStopApp (item) {
        if (!this.selectedCompanyId) {
          this.$toast.error('Select a company first.', 'Error', {
            position: 'topRight',
          })
          return
        }

        this.forceStopDevices = item ? [item] : this.selectedDevices
        this.dialogForceStopApp = true
      },
      async openDialogForceUninstall () {
        if (!this.selectedCompanyId) {
          this.$toast.error('Select a company first.', 'Error', {
            position: 'topRight',
          })
          return
        }

        // No device selection required: the dialog can target a group instead.
        this.uninstallTarget = {
          mode: this.selectedDevices.length ? 'devices' : 'group',
          groupId: null,
          error: '',
        }
        this.loadDeviceGroups()

        this.dialogForceUninstall = true
        this.forceUninstallError = ''
        this.selectedForceUninstallAppId = null
        await this.loadForceUninstallApps()
      },
      async loadForceUninstallApps () {
        this.forceUninstallLoadingApps = true
        this.forceUninstallError = ''

        try {
          const apps = []
          let page = 1
          const pageSize = 200
          let totalCount = 0

          while (page <= 10) {
            const response = await this.$http.post(`/CompanyKioskApp/paginated/${this.selectedCompanyId}`, {
              pageNumber: page,
              pageSize,
              search: '',
            })
            const result = response?.data?.data
            const items = Array.isArray(result?.items) ? result.items : []
            apps.push(...items)
            totalCount = Number(result?.totalCount || items.length)

            if (!items.length || apps.length >= totalCount) {
              break
            }

            page += 1
          }

          const apkOptions = await this.loadUninstallApkOptions()

          this.forceUninstallApps = apps
            .filter(app => Number(app?.id) > 0)
            .map(app => {
              const packageName = String(app?.packageName || '').trim()
              const title = String(app?.title || app?.label || packageName || `App ${app.id}`).trim()
              const matchingApk = apkOptions.find(apk => String(apk?.packageName || '').trim() === packageName)
              return {
                ...app,
                deviceConfigFilePathId: matchingApk?.deviceConfigFilePathId || 0,
                apkVersionName: matchingApk?.versionName || '',
                apkVersionCode: matchingApk?.versionCode || 0,
                displayLabel: packageName && title !== packageName
                  ? `${title} (${packageName})`
                  : title,
              }
            })

          if (!this.forceUninstallApps.length) {
            this.forceUninstallError = 'No registered apps found for this company.'
          }
        } catch (error) {
          this.forceUninstallApps = []
          this.forceUninstallError = 'Could not load registered apps for uninstall.'
        } finally {
          this.forceUninstallLoadingApps = false
        }
      },
      async submitForceUninstall () {
        if (!this.selectedForceUninstallAppId) {
          this.$toast.error('Select an app to uninstall.', 'Error', { position: 'topRight' })
          return
        }

        const targetingGroup = this.uninstallTarget.mode === 'group'

        if (targetingGroup && !this.uninstallTarget.groupId) {
          this.uninstallTarget.error = 'Select a device group'
          this.$toast.error('Select a device group.', 'Error', { position: 'topRight' })
          return
        }

        if (!targetingGroup && !this.selectedDevices.length) {
          this.$toast.error('Select at least one device.', 'Error', { position: 'topRight' })
          return
        }

        const invalidDevice = targetingGroup
          ? null
          : this.selectedDevices.find(device => !this.deviceActionId(device))
        if (invalidDevice) {
          this.$toast.error('One or more selected devices do not have a valid device id.', 'Error', {
            position: 'topRight',
          })
          return
        }

        this.forceUninstallSubmitting = true

        try {
          const companyKioskAppId = Number(this.selectedForceUninstallAppId)
          const selectedApp = this.forceUninstallApps.find(app => Number(app?.id) === companyKioskAppId)
          const deviceConfigFilePathId = Number(selectedApp?.deviceConfigFilePathId || 0)

          if (!deviceConfigFilePathId) {
            this.$toast.error('No APK path id found for selected app in Scripts Downloads.', 'Error', { position: 'topRight' })
            return
          }

          if (targetingGroup) {
            const sent = await this.sendGroupCommand(
              this.uninstallTarget.groupId,
              'ForceUninstallApp',
              { companyKioskAppId, deviceConfigFilePathId },
            )
            if (sent) {
              this.dialogForceUninstall = false
            }
            return
          }

          const results = await Promise.allSettled(
            this.selectedDevices.map(device =>
              this.$http.post(`CompanyKioskApp/${encodeURIComponent(this.deviceActionId(device))}/ForceUninstall`, {
                companyKioskAppId,
                deviceConfigFilePathId,
              }),
            ),
          )
          const failed = results.filter(result =>
            result.status === 'rejected' ||
            result.value?.data?.success === false ||
            result.value?.data?.isSuccess === false,
          )
          const successCount = results.length - failed.length

          if (successCount > 0) {
            this.$toast.success(
              `Uninstall command sent to ${successCount} device${successCount === 1 ? '' : 's'}.`,
              'Success',
              { position: 'topRight' },
            )
          }

          if (failed.length > 0) {
            const firstError = failed[0]?.reason || failed[0]?.value
            this.$toast.error(
              firstError?.response?.data?.message ||
              firstError?.data?.message ||
              'Some uninstall commands failed.',
              'Error',
              { position: 'topRight' },
            )
          }

          if (successCount > 0) {
            this.dialogForceUninstall = false
          }
        } finally {
          this.forceUninstallSubmitting = false
        }
      },
      openDialogFtpProfile (item) {
        this.currentMacAddress = item.macAddress
        this.dialogFtpProfile = true
      },
      openDialogCurrentServer (item) {
        this.currentMacAddress = item.macAddress
        this.dialogCurrentServer = true
      },
      openDeviceNameEdit (item) {
        this.editDeviceNameTargetItem = item
        this.editDeviceNameValue = item?.name || ''
        this.editDeviceNameError = ''
        this.dialogEditDeviceName = true
      },
      openMacAddressEdit (item) {
        if (!this.isSuperUser) {
          this.$toast.error('This action requires the SuperUser role.', 'Error', {
            position: 'topRight',
          })
          return
        }

        this.editMacTargetItem = item
        this.editMacCurrentValue = String(item?.macAddress || '')
        this.editMacValue = String(item?.macAddress || '')
        this.editMacError = ''
        this.dialogEditMacAddress = true
      },
      normalizeMacAddress (value) {
        const hex = String(value || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase()

        if (hex.length !== 12) {
          return ''
        }

        return hex.match(/.{2}/g).join(':')
      },
      async saveMacAddressEdit () {
        if (!this.isSuperUser) {
          this.$toast.error('This action requires the SuperUser role.', 'Error', {
            position: 'topRight',
          })
          return
        }

        const item = this.editMacTargetItem
        const currentMacAddress = String(item?.macAddress || '').trim()

        if (!currentMacAddress) {
          this.editMacError = 'Device has no current MAC address.'
          return
        }

        const newMacAddress = this.normalizeMacAddress(this.editMacValue)

        if (!newMacAddress) {
          this.editMacError = 'Enter a valid MAC address with 6 hex pairs. Example: AA:BB:CC:44:55:66.'
          return
        }

        if (newMacAddress === this.normalizeMacAddress(currentMacAddress)) {
          this.editMacError = 'New MAC address is the same as the current one.'
          return
        }

        this.editMacSaving = true
        try {
          const ok = await this.runDeviceAction(
            () => this.$http.post('Device/ChangeMacAddress', {
              currentMacAddress: currentMacAddress,
              newMacAddress: newMacAddress,
            }),
            'MAC address changed successfully.',
            'Changing MAC address failed.',
          )

          if (ok) {
            item.macAddress = newMacAddress
            this.dialogEditMacAddress = false
            this.getDevicesByCompany()
          }
        } finally {
          this.editMacSaving = false
        }
      },
      validateVehicleNumber (value) {
        const vehicleNumber = String(value || '').trim()

        if (!vehicleNumber) {
          return 'Vehicle number is required.'
        }

        if (!/^[A-Z]+[0-9]*$/.test(vehicleNumber)) {
          return 'Use uppercase letters first, then numbers only. No spaces, symbols, lowercase letters, or letters after numbers.'
        }

        return ''
      },
      saveDeviceNameEdit () {
        if (!this.editDeviceNameTargetItem) {
          return
        }

        const vehicleNumber = String(this.editDeviceNameValue || '').trim()
        const validationError = this.validateVehicleNumber(vehicleNumber)

        if (validationError) {
          this.editDeviceNameError = validationError
          return
        }

        this.editDeviceNameTargetItem.name = vehicleNumber
        this.updateDeviceName(this.editDeviceNameTargetItem)
        this.dialogEditDeviceName = false
      },
      async openDeviceAdditionalInfo (item) {
        const macAddress = String(item?.macAddress || '').trim()
        this.deviceAdditionalInfo = null
        this.deviceAdditionalInfoError = ''
        this.dialogDeviceAdditionalInfo = true

        if (!macAddress) {
          this.deviceAdditionalInfoError = 'No additional info found.'
          return
        }

        this.deviceAdditionalInfoLoading = true
        try {
          const response = await fetch(
            `https://mcabs.softclient.com.au/devices-api/api/DevicePublic/GetDeviceFromQueue?mac=${encodeURIComponent(macAddress)}`,
          )
          const result = await response.json()
          const data = result?.success === true ? result?.data : null

          if (!data) {
            this.deviceAdditionalInfoError = 'No additional info found.'
            return
          }

          this.deviceAdditionalInfo = data
        } catch (error) {
          this.deviceAdditionalInfoError = 'No additional info found.'
        } finally {
          this.deviceAdditionalInfoLoading = false
        }
      },
      // Read any meta already stored on the device. The list exposes it either
      // as a `meta` object or a `metadata` JSON string depending on the source.
      parseDeviceMeta (item) {
        if (item?.meta && typeof item.meta === 'object') {
          return item.meta
        }

        const raw = item?.metadata

        if (raw && typeof raw === 'object') {
          return raw
        }

        if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw)
            if (parsed && typeof parsed === 'object') {
              return parsed
            }
          } catch (error) {
            // Not JSON metadata; fall through to defaults.
          }
        }

        return {}
      },
      buildDeviceMetaRows (item) {
        const existing = this.parseDeviceMeta(item)
        const entries = Object.entries(existing)

        if (entries.length) {
          return entries.map(([label, value]) => ({
            label: String(label || ''),
            value: value == null ? '' : String(value),
          }))
        }

        // Sensible starting point when the device has no stored info yet.
        return [
          { label: 'Name', value: '' },
          { label: 'Phone Number', value: '' },
        ]
      },
      deviceMetaDisplayRows (item) {
        const meta = this.parseDeviceMeta(item)

        return Object.entries(meta)
          .filter(([label]) => String(label || '').trim())
          .map(([label, value]) => {
            const trimmedLabel = String(label || '').trim()
            return {
              label: SHORT_META_LABELS[trimmedLabel.toLowerCase()] || trimmedLabel,
              value: value == null ? '' : String(value),
            }
          })
      },
      openDeviceCommands (item) {
        const deviceId = item?.id ?? item?.deviceId
        const macAddress = item?.macAddress

        if (!deviceId && !macAddress) {
          this.$toast.error('This device has no id or MAC address to track commands with.')
          return
        }

        const query = {}
        if (deviceId) query.deviceId = String(deviceId)
        if (macAddress) query.mac = macAddress

        this.$router.push({ path: '/device-commands', query })
      },
      openDeviceMetaEdit (item) {
        this.deviceMetaTargetItem = item
        this.deviceMetaError = ''
        this.deviceMetaRows = this.buildDeviceMetaRows(item)
        this.dialogDeviceMeta = true
      },
      addDeviceMetaRow () {
        this.deviceMetaRows.push({ label: '', value: '' })
      },
      removeDeviceMetaRow (index) {
        this.deviceMetaRows.splice(index, 1)
      },
      async saveDeviceMetaEdit () {
        const item = this.deviceMetaTargetItem
        const deviceId = item?.id ?? item?.deviceId

        if (!item || !deviceId) {
          this.deviceMetaError = 'This device cannot be updated (missing id).'
          return
        }

        const meta = {}
        const seenLabels = new Set()

        for (const row of this.deviceMetaRows) {
          const label = String(row.label || '').trim()

          if (!label) {
            continue
          }

          const labelKey = label.toLowerCase()
          if (seenLabels.has(labelKey)) {
            this.deviceMetaError = `Duplicate label "${label}". Each label must be unique.`
            return
          }
          seenLabels.add(labelKey)

          meta[label] = String(row.value == null ? '' : row.value)
        }

        this.deviceMetaSaving = true
        this.deviceMetaError = ''

        try {
          const response = await this.$http.put('Device/UpdateMeta', {
            id: deviceId,
            meta,
          })

          if (response?.data?.success === false) {
            this.deviceMetaError = response.data.message || 'Could not save additional info.'
            return
          }

          // Reflect the saved values locally so the row/column stay in sync
          // without needing a full reload.
          this.$set(item, 'meta', meta)
          this.$set(item, 'metadata', this.formatDeviceMetaSummary(meta))

          this.$toast.success('Additional info saved.', 'Success', { position: 'topRight' })
          this.dialogDeviceMeta = false
        } catch (error) {
          this.deviceMetaError =
            error?.response?.data?.message ||
            error?.message ||
            'Could not save additional info.'
          this.$toast.error(this.deviceMetaError, 'Error', { position: 'topRight' })
        } finally {
          this.deviceMetaSaving = false
        }
      },
      formatDeviceMetaSummary (meta) {
        return Object.entries(meta)
          .map(([label, value]) => `${label}: ${value}`)
          .join(', ')
      },
      updateDeviceName (item) {
        this.$http
          .patch('MobileStatus/UpdateMobileStatus', {
            Id: item.id,
            Name: item.name,
            Notes: item.notes,
            InvoiceNumber: item.invoiceNumber,
            StatusId: item.statusId,
          })
          .then((response) => {
            if (response.data.success) {
              this.$toast.success(response.data.message, 'Success', {
                position: 'topRight',
              })
            } else {
              this.$toast.error(response.data.message, 'Error', {
                position: 'topRight',
              })
            }
          })
      },
      applyRequiredVersionUpdates (updates) {
        const items = updates || []

        items.forEach((update) => {
          const deviceIndex = this.mobileStatuses.findIndex(
            device => device.macAddress === update.macAddress,
          )

          if (deviceIndex > -1) {
            const device = {
              ...this.mobileStatuses[deviceIndex],
              requiredVersion: update.requiredVersion,
            }
            this.$set(this.mobileStatuses, deviceIndex, device)
          }

          const selectedDeviceIndex = this.selectedDevices.findIndex(
            device => device.macAddress === update.macAddress,
          )

          if (selectedDeviceIndex > -1) {
            const device = {
              ...this.selectedDevices[selectedDeviceIndex],
              requiredVersion: update.requiredVersion,
            }
            this.$set(this.selectedDevices, selectedDeviceIndex, device)
          }
        })
      },
      updateRequireDownload (item, isRequired) {
        var data = {
          macAddress: item.macAddress,
          requireDownload: isRequired,
        }
        SettingsDataService.updateRequiredDownload(data).then((response) => {
          if (response.data.isSuccess) {
            const index = this.mobileStatuses.findIndex(
              (x) => x.macAddress === item.macAddress,
            )
            if (index > -1) {
              const device = this.mobileStatuses[index]
              device.requireDownload = isRequired
              this.$set(this.mobileStatuses, index, device)
            }
            this.$toast.success(
              'Required status updated successfully.',
              'Success',
              {
                position: 'topRight',
              },
            )
          }
        })
      },
      updateFtpProfile () {
        var data = {
          macAddress: this.currentMacAddress,
          ftpConfigurationId: this.currentFtpProfile,
        }
        SettingsDataService.updateDeviceFtpConfigurations(data).then(
          (response) => {
            if (response.data.isSuccess) {
              this.$toast.success(
                'Required status updated successfully.',
                'Success',
                {
                  position: 'topRight',
                },
              )
              this.getDataFromApi()
            }
          },
        )
        this.dialogFtpProfile = false
      },
      updateServerUrl () {
        var data = {
          macAddress: this.currentMacAddress,
          settingId: this.currentServer,
        }
        SettingsDataService.updateCurrentServerUrl(data).then((response) => {
          if (response.data.isSuccess) {
            this.$toast.success('Server url updated successfully.', 'Success', {
              position: 'topRight',
            })
            this.getDataFromApi()
          }
          this.dialogCurrentServer = false
        })
      },
      isRequiredDownload () {
        this.currentRequiredDownload = !this.currentRequiredDownload
      },
      bulkDeviceUpdate () {
        var macAddressList = []
        this.selectedDevices.forEach((x) => {
          macAddressList.push(x.macAddress)
        })
        var data = {
          macAddresses: macAddressList,
          settingId: this.currentServer,
          ftpConfigurationId: this.currentFtpProfile,
          requireDownload: this.currentRequiredDownload,
          requiredVersion: this.currentRequiredVersion,
        }
        SettingsDataService.updateDeviceConfigurations(data).then((response) => {
          if (response.data.isSuccess) {
            this.$toast.success('Server url updated successfully.', 'Success', {
              position: 'topRight',
            })
            this.getDataFromApi()
          }
        })
        this.dialogBulkDeviceUpdate = false
      },
      openDialogDeleteLocalData (item) {
        this.currentUserName = item.username
        this.dialogDeleteLocalData = true
      },
      deleteOrderProductsFromLocalDb () {
        this.$http
          .get('/Device/DeleteOrderProducts', {
            params: { username: this.currentUserName },
          })
          .then((response) => { })
          .catch(() => {
            this.$toast.error('Something went wrong.', 'Error', {
              position: 'topRight',
            })
          })
      },
      deleteFailedPickingProductsFromLocalDb () {
        this.$http
          .get('/Device/DeletePickingFailedProducts', {
            params: { username: this.currentUserName },
          })
          .then((response) => { })
          .catch(() => {
            this.$toast.error('Something went wrong.', 'Error', {
              position: 'topRight',
            })
          })
      },
      saveChanges () {
        // console.log("devices", this.selectedDevices);
        //  console.log("actions", this.workingActions);
        this.isLoading = true
        var data = new FormData()
        this.workingActions.forEach((item, index) => {
          if (item.id && typeof item.id !== 'undefined') {
            data.append(`deviceFiles[${index}].id`, item.id)
          }
          if (item.deviceFilePath && typeof item.deviceFilePath !== 'undefined') {
            data.append(
              `deviceFiles[${index}].deviceFilePath`,
              item.deviceFilePath,
            )
          }
          if (item.file) {
            data.append(`deviceFiles[${index}].file`, item.file)
          }
          if (item.action && item.action !== undefined) {
            data.append(`deviceFiles[${index}].action`, item.action)
          }
          if (item.version && typeof item.version !== 'undefined') {
            data.append(`deviceFiles[${index}].version`, item.version)
          }
          if (item.package && typeof item.package !== 'undefined') {
            data.append(`deviceFiles[${index}].package`, item.package)
          }
          if (item.icon) {
            const fileName = item.file.name.split('.').slice(0, -1).join('.')
            const imageName = fileName + '.jpg'
            data.append(
              `deviceFiles[${index}].image`,
              this.dataURLtoFile(item.icon, imageName),
            )
          }
        })

        this.selectedDevices.forEach((item, index) => {
          data.append(`devices[${index}]`, item.id)
        })

        this.$http
          .post('DeviceFile/UpdateFileActionListToDevices', data)
          .then((response) => {
            if (response.data.success) {
              this.$toast.success(response.data.message, 'Success', {
                position: 'topRight',
              })
            } else {
              this.$toast.error(response.data.message, 'Error', {
                position: 'topRight',
              })
            }
            this.isLoading = false
          })
          .catch(() => {
            this.isLoading = false
          })
      },
      generateReport () {
        this.$http.get('Device/GetDevicesReport').then((response) => {
          try {
            if (response.data && response.data.length > 0) {
              const fields = response.data[0].keys
              const opts = { fields }
              const csv = parse(response.data, opts)
              var fileUrl = window.URL.createObjectURL(new Blob([csv]))
              var fileLink = document.createElement('a')
              fileLink.href = fileUrl
              fileLink.setAttribute('download', 'MyDevices_Report.csv')
              document.body.appendChild(fileLink)
              fileLink.click()
            // console.log(csv);
            }
          } catch (err) {
            console.error(err)
          }
        })
      },
      dataURLtoFile (dataurl, filename) {
        var arr = dataurl.split(',')
        var mime = arr[0].match(/:(.*?);/)[1]
        var bstr = atob(arr[1])
        var n = bstr.length
        var u8arr = new Uint8Array(n)

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }

        return new File([u8arr], filename, { type: mime })
      },
      getCompany (_company) {
        if (_company) {
          const names = String(_company)
            .split(/->|\/|>/)
            .map(name => name.trim())
            .filter(Boolean)
          if (names.length > 0) {
            return names[names.length - 1]
          }
        }
        return _company
      },
      getNormalizedDeviceMac (item) {
        return String(item?.macAddress || item?.deviceId || item?.id || '')
          .trim()
          .toLowerCase()
      },
      getCompanyEntryById (companyId) {
        const normalizedCompanyId = Number(companyId) || 0
        if (!normalizedCompanyId) {
          return null
        }

        return store.state.company?.companies?.[normalizedCompanyId] || null
      },
      getCompanyDepth (companyId) {
        const company = this.getCompanyEntryById(companyId)
        return Array.isArray(company?.path) ? company.path.length : 0
      },
      getCompanyTextDepth (company) {
        if (!company) {
          return 0
        }

        return String(company)
          .split(/->|\/|>/)
          .map(name => name.trim())
          .filter(Boolean)
          .length
      },
      resolveDeviceCompanyFromItem (item) {
        const itemCompanyId = Number(item?.companyId) || 0
        const companyFromStore = itemCompanyId > 0
          ? this.getCompanyEntryById(itemCompanyId)
          : null

        if (companyFromStore) {
          const fullName = companyFromStore.pathStr || companyFromStore.name || ''
          const name = companyFromStore.name || this.getCompany(fullName) || ''

          if (name || fullName) {
            return {
              companyId: itemCompanyId,
              depth: this.getCompanyDepth(itemCompanyId),
              name,
              fullName: fullName || name,
            }
          }
        }

        const companyValue =
          item?.companyPath ||
          item?.pathStr ||
          item?.companyName ||
          item?.company

        if (companyValue) {
          const fullName = String(companyValue)
          const name = this.getCompany(fullName) || fullName

          return {
            companyId: itemCompanyId,
            depth: this.getCompanyTextDepth(fullName),
            name,
            fullName,
          }
        }

        return null
      },
      rememberDeviceCompany (item) {
        const mac = this.getNormalizedDeviceMac(item)
        if (!mac) {
          return
        }

        const resolvedCompany = this.resolveDeviceCompanyFromItem(item)
        if (!resolvedCompany?.name) {
          return
        }

        const cached = this.deviceCompanyByMac[mac]

        if (!cached || (resolvedCompany.depth || 0) >= (cached.depth || 0)) {
          this.$set(this.deviceCompanyByMac, mac, resolvedCompany)
        }
      },
      getTaxiCompanyId (item) {
        return Number(item?.companyId) || Number(this.selectedCompanyId) || 0
      },
      loadTaxiLinkInfo (devices) {
        const companyIds = [...new Set((devices || []).map(device => this.getTaxiCompanyId(device)))]
          .filter(id => id > 0 && !this.taxiLinkInfoByCompanyId[id])

        companyIds.forEach((companyId) => {
          // Placeholder so parallel reloads don't refetch the same company.
          this.$set(this.taxiLinkInfoByCompanyId, companyId, {
            domainUrl: '',
            hasMytaxis: false,
            loading: true,
          })

          Promise.all([
            this.$http.get(`Company/${encodeURIComponent(companyId)}`).catch(() => null),
            this.$http.post(`/CompanyKioskApp/paginated/${companyId}`, { pageNumber: 1, pageSize: 500 }).catch(() => null),
          ]).then(([companyResponse, appsResponse]) => {
            const company = companyResponse?.data?.data || companyResponse?.data || {}
            let meta = company.meta || company.Meta || {}
            if (typeof meta === 'string') {
              try {
                meta = JSON.parse(meta) || {}
              } catch (error) {
                meta = {}
              }
            }

            const findMetaValue = key => {
              const entry = Object.entries(meta)
                .find(([label]) => String(label || '').trim().toLowerCase() === key)
              return entry ? String(entry[1] || '').trim() : ''
            }
            const apps = appsResponse?.data?.data?.items || []
            const hasMytaxis = apps.some(app =>
              /mytaxi/i.test(String(app?.packageName || '')) || /mytaxi/i.test(String(app?.title || app?.label || '')),
            )

            this.$set(this.taxiLinkInfoByCompanyId, companyId, {
              domainUrl: findMetaValue('domain url'),
              hasMytaxis,
              loading: false,
            })
          })
        })
      },
      getAppVersionInfo (item) {
        const info = this.taxiLinkInfoByCompanyId[this.getTaxiCompanyId(item)]
        return {
          mydeviceVersion: item?.appVersion || 'Unknown',
          mydeviceDefault: item?.myDeviceDefaultVersion || 'Not set',
          hasMytaxi: !!info?.hasMytaxis || !!item?.myTaxiAppVersion || !!item?.myTaxiDefaultVersion,
          mytaxiVersion: item?.myTaxiAppVersion || 'Not installed',
          mytaxiDefault: item?.myTaxiDefaultVersion || 'Not set',
        }
      },
      getTaxiLink (item) {
        const vehicleNo = String(item?.name || '').trim()
        const info = this.taxiLinkInfoByCompanyId[this.getTaxiCompanyId(item)]
        if (!vehicleNo || !info?.hasMytaxis || !info.domainUrl) {
          return ''
        }

        let domain = info.domainUrl.replace(/\/+$/, '')
        if (!/^https?:\/\//i.test(domain)) {
          domain = `https://${domain}`
        }

        return `${domain}/taxis?search=${encodeURIComponent(vehicleNo)}`
      },
      getDeviceCompanyName (item, full = false) {
        const itemCompany = this.resolveDeviceCompanyFromItem(item)
        if (itemCompany?.name) {
          return full ? (itemCompany.fullName || itemCompany.name) : itemCompany.name
        }

        const mac = this.getNormalizedDeviceMac(item)
        const cachedCompany = mac ? this.deviceCompanyByMac[mac] : null
        if (cachedCompany?.name) {
          return full ? (cachedCompany.fullName || cachedCompany.name) : cachedCompany.name
        }

        const selectedCompanyValue = full
          ? this.selectedCompanyPath?.map(company => company?.name).filter(Boolean).join(' / ') || this.selectedCompanyName
          : this.selectedCompanyName

        if (selectedCompanyValue) {
          return selectedCompanyValue
        }

        return ''
      },
      async getCompanyConfigurations (id) {
        try {
          const res = await this.$http(`/CompanyConfiguration?companyId=${id}`)
          if (res.data?.success) {
            return res.data.data || []
          }
        } catch (error) {

        }

        return []
      },
      chooseReference () {
        if (!this.referenceId || this.referenceId === 0) {
          return
        }

        this.getCompanyConfigurations(this.referenceId).then(data => {
          this.editedItems = data.map(x => ({
            name: x.name,
            type: x.type,
            value: x.value,
            description: x.description,
          }))
        })
      },
      addConfigurations () {

      },
      closeConfigurations () {
        this.dialogConfigurations = false
        this.editedItems = []
      },
      // "" when the default applies, otherwise "&ttlMinutes=N".
      ttlQuery (expiry) {
        const minutes = buildTtlMinutes(expiry)
        return minutes ? `&ttlMinutes=${minutes}` : ''
      },
      sendReboot (editedItem, { item }) {
        const ttl = this.ttlQuery(this.rebootExpiry)
        this.$http.get(`/Message/SendRebootCall?deviceId=${item.id}&seconds=${editedItem.seconds}${ttl}`).then((response) => {
          if (response.data.success) {
            this.$toast.success('Command sent successfully.', 'Success', {
              position: 'topRight',
            })
          } else {
            this.$toast.error(response.data.message, 'Error', {
              position: 'topRight',
            })
          }
        })
      },
      kioskPinRule (v) {
        const s = v != null ? String(v) : ''
        if (s.length !== 6) return 'Enter 6 digits'
        if (!/^\d{6}$/.test(s)) return 'Digits only'
        return true
      },
      kioskPinMatchRule (v) {
        return String(v) === String(this.kioskPinNew) || 'PINs must match'
      },
      openDialogSetKioskPin () {
        if (!this.selectedCompanyId) {
          this.$toast.error('Please select a company.', 'Error', { position: 'topRight' })
          return
        }
        this.kioskPinNew = ''
        this.kioskPinNewConfirm = ''
        this.dialogSetKioskPin = true
      },
      async saveKioskUnlockPin () {
        if (this.kioskPinRule(this.kioskPinNew) !== true || this.kioskPinRule(this.kioskPinNewConfirm) !== true) {
          this.$toast.error('Enter a valid 6-digit PIN.', 'Error', { position: 'topRight' })
          return
        }
        if (String(this.kioskPinNew) !== String(this.kioskPinNewConfirm)) {
          this.$toast.error('PINs do not match.', 'Error', { position: 'topRight' })
          return
        }
        this.kioskPinSaving = true
        try {
          const res = await this.$http.post('/Company/SetKioskUnlockPin', {
            companyId: this.selectedCompanyId,
            pin: String(this.kioskPinNew),
          })
          const ok = res.data?.success === true || res.data?.isSuccess === true
          if (ok) {
            this.$toast.success('Kiosk unlock PIN saved.', 'Success', { position: 'topRight' })
            this.dialogSetKioskPin = false
          } else {
            this.$toast.error(res.data?.message || 'Could not save PIN.', 'Error', { position: 'topRight' })
          }
        } catch (e) {
          this.$toast.error(e.response?.data?.message || e.message || 'Could not save PIN.', 'Error', { position: 'topRight' })
        } finally {
          this.kioskPinSaving = false
        }
      },
      deviceActionId (item) {
        return item?.id || item?.deviceId || ''
      },
      deviceActionDisplayLabel (item) {
        const primaryLabel =
          item?.deviceModel || item?.name || item?.macAddress || item?.deviceId || item?.id || 'Unknown device'
        const deviceId = item?.macAddress || item?.deviceId || ''

        if (!deviceId) {
          return primaryLabel
        }

        return `${primaryLabel} (Device ID: ${deviceId})`
      },
      async runDeviceAction (request, successMessage, errorMessage) {
        try {
          const response = await request()
          const ok = response?.data?.success !== false && response?.data?.isSuccess !== false
          if (ok) {
            this.$toast.success(response?.data?.message || successMessage, 'Success', { position: 'topRight' })
            return true
          }
          this.$toast.error(response?.data?.message || response?.data?.error || errorMessage, 'Error', { position: 'topRight' })
        } catch (e) {
          this.$toast.error(
            e.response?.data?.message || e.response?.data?.error || e.message || errorMessage,
            'Error',
            { position: 'topRight' },
          )
        }
        return false
      },
      setDeviceSyncLoading (item, value) {
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          return
        }
        this.$set(this.deviceSyncLoadingById, String(deviceId), value)
      },
      isDeviceSyncLoading (item) {
        const deviceId = this.deviceActionId(item)
        return Boolean(deviceId && this.deviceSyncLoadingById[String(deviceId)])
      },
      async triggerDeviceSync (item) {
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for sync.', 'Error', { position: 'topRight' })
          return
        }

        this.setDeviceSyncLoading(item, true)
        try {
          await this.runDeviceAction(
            () => this.$http.post('/Message/TriggerDeviceSync', null, {
              params: {
                deviceId,
                reason: 'Manual device sync requested from Devices page',
              },
            }),
            'Device sync triggered successfully.',
            'Device sync failed.',
          )
        } finally {
          this.setDeviceSyncLoading(item, false)
        }
      },
      async triggerCompanySync () {
        if (!this.selectedCompanyId) {
          this.$toast.error('Please select a company.', 'Error', { position: 'topRight' })
          return
        }

        this.companySyncSending = true
        try {
          await this.runDeviceAction(
            () => this.$http.post('/Message/TriggerCompanySync', null, {
              params: {
                companyId: this.selectedCompanyId,
                reason: 'Manual company sync requested from Devices page',
              },
            }),
            'Company sync triggered successfully.',
            'Company sync failed.',
          )
        } finally {
          this.companySyncSending = false
        }
      },
      // Per-type expiry defaults published by /device-groups/command-types.
      expiryInfoFor (type) {
        const catalog = this.groupCommandCatalog
        return (catalog && catalog.expiryByType && catalog.expiryByType[type]) || null
      },
      resetExpiry () {
        return { mode: 'default', ttlMinutes: null, expiresAt: null }
      },
      openRebootDialog (item) {
        this.rebootExpiry = this.resetExpiry()
        this.loadGroupCommandCatalog()
        this.dialogReboot.show(undefined, { formTitle: 'Send Reboot Command', item })
      },
      openLockDialog (item) {
        this.lockTargetItem = item
        this.lockExpiry = this.resetExpiry()
        this.loadGroupCommandCatalog()
        this.dialogLockDevice = true
      },
      async confirmLockDevice () {
        const item = this.lockTargetItem
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }
        this.lockSending = true
        try {
          const ok = await this.runDeviceAction(
            () => this.$http.post(`Message/LockDevice?deviceId=${encodeURIComponent(deviceId)}${this.ttlQuery(this.lockExpiry)}`),
            'Lock command sent successfully.',
            'Lock device failed.',
          )
          if (ok) {
            this.dialogLockDevice = false
          }
        } finally {
          this.lockSending = false
        }
      },
      openUnlockDialog (item) {
        this.unlockTargetItem = item
        this.unlockExpiry = this.resetExpiry()
        this.loadGroupCommandCatalog()
        this.dialogUnlockDevice = true
      },
      async confirmUnlockDevice () {
        const item = this.unlockTargetItem
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }
        this.unlockSending = true
        try {
          const ok = await this.runDeviceAction(
            () => this.$http.post(`Message/UnlockDevice?deviceId=${encodeURIComponent(deviceId)}${this.ttlQuery(this.unlockExpiry)}`),
            'Unlock command sent successfully.',
            'Unlock device failed.',
          )
          if (ok) {
            this.dialogUnlockDevice = false
          }
        } finally {
          this.unlockSending = false
        }
      },
      openDialogUnlockCodes () {
        if (!this.selectedCompanyId) {
          this.$toast.error('Please select a company.', 'Error', { position: 'topRight' })
          return
        }

        // When devices are ticked, scope the dialog to just those rows.
        this.unlockCodeSelectedKeys = this.buildUnlockCodeSelectionKeys(this.selectedDevices)
        this.dialogUnlockCodes = true
        this.loadUnlockCodeDevices()
      },
      buildUnlockCodeSelectionKeys (devices) {
        const keys = new Set()

        ;(devices || []).forEach(device => {
          const id = String(this.deviceActionId(device) || '').trim()
          if (id) {
            keys.add(`id:${id}`)
          }

          const mac = String(device?.macAddress || device?.deviceMacAddress || '').trim().toLowerCase()
          if (mac) {
            keys.add(`mac:${mac}`)
          }
        })

        return [...keys]
      },
      unlockCodeMatchesSelection (device, selectedSet) {
        const id = String(this.deviceActionId(device) || '').trim()
        if (id && selectedSet.has(`id:${id}`)) {
          return true
        }

        const mac = String(device?.macAddress || device?.deviceMacAddress || '').trim().toLowerCase()
        return !!(mac && selectedSet.has(`mac:${mac}`))
      },
      clearUnlockCodeSelectionFilter () {
        this.unlockCodeSelectedKeys = []
      },
      async loadUnlockCodeDevices () {
        if (!this.selectedCompanyId) {
          this.unlockCodeDevices = []
          return
        }

        this.unlockCodesLoading = true
        try {
          const response = await this.$http.get(`DeviceSecurityDetails/company/${encodeURIComponent(this.selectedCompanyId)}/unlock-audit`, {
            params: {
              take: 100,
            },
          })
          const rows = Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : []

          this.unlockCodeDevices = rows.map(row => {
            const device = this.normalizeUnlockCodeDevice(row)
            this.rememberDeviceCompany(device)
            return this.applyUnlockCodeAuditSummary(device)
          })
        } catch (e) {
          this.unlockCodeDevices = []
          this.$toast.error(e.response?.data?.message || e.message || 'Could not load company devices.', 'Error', { position: 'topRight' })
        } finally {
          this.unlockCodesLoading = false
        }
      },
      normalizeUnlockCodeDevice (row) {
        return {
          id: row.deviceId,
          deviceId: row.deviceId,
          name: row.deviceName || '',
          deviceName: row.deviceName || '',
          macAddress: row.deviceMacAddress || '',
          deviceMacAddress: row.deviceMacAddress || '',
          companyId: row.companyId || this.selectedCompanyId,
          deviceModel: row.deviceModel || '',
          lastConnected: row.lastConnectedAt || null,
          emergencyCode: null,
          emergencyCodeVersion: row.emergencyCodeVersion,
          emergencyCodeViewedAt: row.emergencyCodeViewedAt || null,
          emergencyCodeVersionFromAudit: row.emergencyCodeVersion || null,
          emergencyCodeViewedBy: row.emergencyCodeViewedBy || null,
          emergencyCodeExpiresAt: row.emergencyCodeExpiresAt || null,
          emergencyCodeLastSyncedAt: row.emergencyCodeLastSyncedAt || null,
          emergencyCodeLastUsedAt: row.emergencyCodeLastUsedAt || null,
          hasEmergencyCode: !!row.hasEmergencyCode,
          isViewed: !!row.isViewed,
          isExpired: !!row.isExpired,
          emergencyCodeDisplay: row.emergencyCodeDisplay || (row.isViewed ? 'Viewed previously' : 'Not loaded'),
          statusDisplay: row.statusDisplay || 'Ready',
          primaryUnlockEventId: null,
          primaryUnlockQueued: null,
        }
      },
      unlockCodeDeviceKey (item) {
        return String(this.deviceActionId(item) || item?.macAddress || '')
      },
      unlockCodeAuditSummaryCacheKey () {
        return `mydevices:unlock-code-audit-summary:${this.$store.getters['authUser/currentCompanyId'] || 'all'}`
      },
      loadUnlockCodeAuditSummaryCache () {
        try {
          const stored = window.sessionStorage.getItem(this.unlockCodeAuditSummaryCacheKey())
          const parsed = stored ? JSON.parse(stored) : {}
          this.unlockCodeAuditSummaryByDevice = parsed && typeof parsed === 'object' ? parsed : {}
        } catch (e) {
          this.unlockCodeAuditSummaryByDevice = {}
        }
      },
      saveUnlockCodeAuditSummaryCache () {
        try {
          window.sessionStorage.setItem(
            this.unlockCodeAuditSummaryCacheKey(),
            JSON.stringify(this.unlockCodeAuditSummaryByDevice),
          )
        } catch (e) {
          // Ignore storage failures; the live row state still updates.
        }
      },
      rememberUnlockCodeViewed (item, occurredAt, codeVersion) {
        if (!item) {
          return
        }

        const summary = {
          emergencyCodeViewedAt: occurredAt || new Date().toISOString(),
          emergencyCodeVersionFromAudit: codeVersion || null,
        }
        this.$set(this.unlockCodeAuditSummaryByDevice, this.unlockCodeDeviceKey(item), summary)
        this.$set(item, 'emergencyCodeViewedAt', summary.emergencyCodeViewedAt)
        this.$set(item, 'emergencyCodeVersionFromAudit', summary.emergencyCodeVersionFromAudit)
        this.saveUnlockCodeAuditSummaryCache()
      },
      applyUnlockCodeAuditSummary (item) {
        if (item.emergencyCodeViewedAt || item.isViewed) {
          return item
        }

        const summary = this.unlockCodeAuditSummaryByDevice[this.unlockCodeDeviceKey(item)]
        if (!summary) {
          return item
        }

        return {
          ...item,
          emergencyCodeViewedAt: summary.emergencyCodeViewedAt || item.emergencyCodeViewedAt || null,
          emergencyCodeVersionFromAudit: summary.emergencyCodeVersionFromAudit || item.emergencyCodeVersionFromAudit || null,
          isViewed: true,
          emergencyCodeDisplay: item.emergencyCodeDisplay === 'Not loaded'
            ? 'Viewed previously'
            : item.emergencyCodeDisplay,
          statusDisplay: item.statusDisplay === 'Ready'
            ? `Code v${summary.emergencyCodeVersionFromAudit || '-'} viewed ${this.formatUnlockDate(summary.emergencyCodeViewedAt)}`
            : item.statusDisplay,
        }
      },
      async loadUnlockCodeAuditStatuses () {
        const devices = this.unlockCodeDevices.filter(device => !device.emergencyCodeViewedAt)

        for (let index = 0; index < devices.length; index += 4) {
          await Promise.all(devices.slice(index, index + 4).map(device => this.loadUnlockCodeAuditStatus(device)))
        }
      },
      async loadUnlockCodeAuditStatus (item) {
        const deviceId = this.deviceActionId(item)
        if (!deviceId || item.emergencyCodeViewedAt) {
          return
        }

        this.setUnlockCodeDeviceLoading('audit-status', item, true)
        try {
          const response = await this.$http.get(`DeviceSecurityDetails/${encodeURIComponent(deviceId)}/unlock-audit?take=20`)
          if (response.data?.success === false) {
            return
          }

          const latestView = (response.data?.data || []).find(audit => audit?.action === 'EmergencyCodeViewed')
          if (latestView) {
            this.rememberUnlockCodeViewed(item, latestView.occurredAt || null, latestView.emergencyCodeVersion || null)
          }
        } catch (e) {
          // Keep the row usable; the explicit Audit button will still show any error.
        } finally {
          this.setUnlockCodeDeviceLoading('audit-status', item, false)
        }
      },
      setUnlockCodeDeviceLoading (action, item, value) {
        const key = `${action}:${this.unlockCodeDeviceKey(item)}`
        this.$set(this.unlockCodeLoadingByDevice, key, value)
      },
      isUnlockCodeDeviceLoading (action, item) {
        const key = `${action}:${this.unlockCodeDeviceKey(item)}`
        return this.unlockCodeLoadingByDevice[key] === true
      },
      async sendPrimaryUnlockFromCodes (item) {
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }

        this.setUnlockCodeDeviceLoading('primary', item, true)
        try {
          const response = await this.$http.post(`DeviceSecurityDetails/${encodeURIComponent(deviceId)}/primary-unlock`)
          const ok = response?.data?.success !== false && response?.data?.isSuccess !== false
          if (ok) {
            this.$set(item, 'primaryUnlockEventId', response?.data?.data?.eventId || null)
            this.$set(item, 'primaryUnlockQueued', response?.data?.data?.queued === true)
            this.$toast.success(response?.data?.message || 'Primary unlock sent successfully.', 'Success', { position: 'topRight' })
          } else {
            this.$toast.error(response?.data?.message || response?.data?.error || 'Primary unlock failed.', 'Error', { position: 'topRight' })
          }
        } catch (e) {
          this.$toast.error(
            e.response?.data?.message || e.response?.data?.error || e.message || 'Primary unlock failed.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.setUnlockCodeDeviceLoading('primary', item, false)
        }
      },
      async viewEmergencyCode (item) {
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }

        this.setUnlockCodeDeviceLoading('view', item, true)
        try {
          const response = await this.$http.get(`DeviceSecurityDetails/${encodeURIComponent(deviceId)}/emergency-code`)
          if (response.data?.success !== false && response.data?.data) {
            this.$set(item, 'emergencyCode', response.data.data)
            this.$set(item, 'hasEmergencyCode', true)
            this.$set(item, 'isExpired', !!response.data.data.isExpired)
            this.$set(item, 'emergencyCodeDisplay', 'Viewed previously')
            this.rememberUnlockCodeViewed(item, new Date().toISOString(), response.data.data.codeVersion)
          } else {
            this.$toast.error(response.data?.message || 'Could not load emergency code.', 'Error', { position: 'topRight' })
          }
        } catch (e) {
          this.$toast.error(e.response?.data?.message || e.message || 'Could not load emergency code.', 'Error', { position: 'topRight' })
        } finally {
          this.setUnlockCodeDeviceLoading('view', item, false)
        }
      },
      async rotateEmergencyCode (item) {
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }

        this.setUnlockCodeDeviceLoading('rotate', item, true)
        try {
          const response = await this.$http.post(`DeviceSecurityDetails/${encodeURIComponent(deviceId)}/emergency-code/rotate`)
          if (response.data?.success !== false && response.data?.data) {
            this.$set(item, 'emergencyCode', response.data.data)
            this.$set(item, 'hasEmergencyCode', true)
            this.$set(item, 'isExpired', !!response.data.data.isExpired)
            this.$set(item, 'emergencyCodeDisplay', 'Viewed previously')
            this.$set(item, 'statusDisplay', `Code v${response.data.data.codeVersion || '-'} viewed ${this.formatUnlockDate(new Date().toISOString())}`)
            this.rememberUnlockCodeViewed(item, new Date().toISOString(), response.data.data.codeVersion)
            this.$toast.success(response.data?.message || 'Emergency unlock code rotated successfully.', 'Success', { position: 'topRight' })
          } else {
            this.$toast.error(response.data?.message || 'Could not rotate emergency code.', 'Error', { position: 'topRight' })
          }
        } catch (e) {
          this.$toast.error(e.response?.data?.message || e.message || 'Could not rotate emergency code.', 'Error', { position: 'topRight' })
        } finally {
          this.setUnlockCodeDeviceLoading('rotate', item, false)
        }
      },
      async openUnlockAudit (item) {
        const targetItem = item || this.unlockTargetItem
        const deviceId = this.deviceActionId(targetItem)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }

        this.unlockAuditTargetItem = targetItem
        this.dialogUnlockAudit = true
        this.unlockAuditLoading = true
        this.setUnlockCodeDeviceLoading('audit', targetItem, true)
        try {
          const response = await this.$http.get(`DeviceSecurityDetails/${encodeURIComponent(deviceId)}/unlock-audit?take=50`)
          if (response.data?.success !== false) {
            this.unlockAudit = response.data?.data || []
            const latestView = this.unlockAudit.find(audit => audit?.action === 'EmergencyCodeViewed')
            if (latestView && targetItem) {
              this.rememberUnlockCodeViewed(targetItem, latestView.occurredAt || null, latestView.emergencyCodeVersion || null)
            }
          } else {
            this.$toast.error(response.data?.message || 'Could not load unlock audit history.', 'Error', { position: 'topRight' })
          }
        } catch (e) {
          this.$toast.error(e.response?.data?.message || e.message || 'Could not load unlock audit history.', 'Error', { position: 'topRight' })
        } finally {
          this.unlockAuditLoading = false
          this.setUnlockCodeDeviceLoading('audit', targetItem, false)
        }
      },
      openUnenrollDialog (item) {
        if (!this.isSuperUser) {
          this.$toast.error('This action requires the SuperUser role.', 'Error', {
            position: 'topRight',
          })
          return
        }
        this.unenrollTargetItem = item
        this.dialogUnenrollDevice = true
      },
      openEnrollDialog (item) {
        this.enrollTargetItem = item
        this.dialogEnrollDevice = true
      },
      async confirmEnrollDevice () {
        const item = this.enrollTargetItem
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }
        this.enrollSending = true
        try {
          const ok = await this.runDeviceAction(
            () => this.$http.post(`Device/${encodeURIComponent(deviceId)}/enroll`),
            'Enroll command sent successfully.',
            'Enroll failed.',
          )
          if (ok) {
            this.dialogEnrollDevice = false
          }
        } finally {
          this.enrollSending = false
        }
      },
      async confirmUnenrollDevice () {
        if (!this.isSuperUser) {
          this.$toast.error('This action requires the SuperUser role.', 'Error', {
            position: 'topRight',
          })
          return
        }
        const item = this.unenrollTargetItem
        const deviceId = this.deviceActionId(item)
        if (!deviceId) {
          this.$toast.error('Device has no id for commands.', 'Error', { position: 'topRight' })
          return
        }
        this.unenrollSending = true
        try {
          const ok = await this.runDeviceAction(
            () => this.$http.post(`Device/${encodeURIComponent(deviceId)}/unenroll`),
            'Unenroll command sent successfully.',
            'Unenroll failed.',
          )
          if (ok) {
            this.dialogUnenrollDevice = false
          }
        } finally {
          this.unenrollSending = false
        }
      },
      sendXmlCommand (editedItem) {
        if (this.xmlTarget.mode === 'group') {
          if (!this.xmlTarget.groupId) {
            this.xmlTarget.error = 'Select a device group'
            this.$toast.error('Select a device group', 'Error', { position: 'topRight' })
            return
          }
          this.sendGroupCommand(this.xmlTarget.groupId, 'SendXmlCommand', { info: editedItem.info })
          return
        }

        if (!this.selectedDevices.length) {
          this.$toast.error('No device selected', 'Error', {
            position: 'topRight',
          })
          return
        }

        this.selectedDevices.forEach(device => {
          this.$http.post(`/Message/SendXMLCommandTodevice?deviceId=${device.id}&info=${editedItem.info}`).then((response) => {
            if (response.data.success) {
              this.$toast.success(`Command sent successfully. Device:${device.name || device.id}`, 'Success', {
                position: 'topRight',
              })
            } else {
              this.$toast.error(response.data.message + ` Device:${device.name || device.id}`, 'Error', {
                position: 'topRight',
              })
            }
          })
        })
      },
      async addNewDevice (editedItem) {
        if (!this.selectedCompanyId) {
          this.$toast.error('Please select the company.', 'Error', { position: 'topRight' })
          return
        }

        if (editedItem.deviceId?.trim()?.length === 0) {
          this.$toast.error('Please enter the device id.', 'Error', { position: 'topRight' })
          return
        }

        const deviceRes = await this.$http.post('/Device', {
          image: '',
          deviceModel: '',
          macAddress: editedItem.deviceId,
          os: '',
          appVersion: '',
          username: '',
          ipAddress: '',
          zone: '',
          signalStrength: '',
          bssid: '',
          linkSpeed: '',
          essid: '',
          latitude: 0,
          longitude: 0,
          licenseNumber: '',
          installedApps: [],
          appInfoList: [],
        })

        if (!deviceRes.data.isSuccess) {
          this.$toast.error(deviceRes.data.message, 'Error', {
            position: 'topRight',
          })

          return
        }

        const res = await this.$http.post('/Company/AddDeviceToCompany', {
          companyId: this.selectedCompanyId,
          deviceId: editedItem.deviceId,
        })

        if (res.data.success) {
          this.$toast.success('Device created successfully.', 'Success', {
            position: 'topRight',
          })
        } else {
          this.$toast.error(deviceRes.data.message, 'Error', {
            position: 'topRight',
          })
        }
      },

      async deleteTelnetHostsDetails (editedItem, form, { item }) {
        const formIndex = form.hosts.findIndex(x => x.id === editedItem.id)
        form.hosts.splice(formIndex, 1)

        // const response = await this.$http.delete(`/DeviceSecurityDetails/${editedItem.id}`)
        // if (response.data.success) {
        //   const formIndex = form.hosts.findIndex(x => x.id === editedItem.id)
        //   form.hosts.splice(formIndex, 1)

        //   const index = item.telnetHosts.findIndex(x => x.id === editedItem.id)
        //   item.telnetHosts.splice(index, 1)

        //   this.$toast.success('Deleted successfully.', 'Success', {
        //     position: 'topRight',
        //   })
        // } else {
        //   this.$toast.error(response.data.message, 'Error', {
        //     position: 'topRight',
        //   })
        // }
      },

      async showTelnetHostsDetails (item) {
        if (item.telnetHosts == null || item.telnetHosts.length === 0) {
          const res = await this.$http(`/DeviceSecurityDetails/${item.id}/device-security-details`)
          item.telnetHosts = res.data?.data?.hosts || []
          item.telnetExpiry = res.data?.data?.expiry
        }

        const formHosts = item.telnetHosts.slice()
        if (formHosts.length === 0) {
          formHosts.push(Object.assign({}, this.defaultTelnet))
        }

        let formExpiry = item.telnetExpiry
        if (!formExpiry) {
          formExpiry = this.moment().add('month', 6).format('YYYY-MM-DD')
        } else {
          formExpiry = this.moment(formExpiry).format('YYYY-MM-DD')
        }

        this.dialogTelnetHostsDetails.show({ hosts: formHosts, expiry: formExpiry, active: item.active }, { formTitle: 'Telnet Server Details', item })
      },

      async fetchTelnetToken (item) {
        const tokenRes = await this.$http(`/DevicesQRAuthentication/generate-QR-code?deviceId=${item.id}`)
        if (tokenRes.data.success) {
          item.telnetToken = tokenRes.data.data.token

          this.$toast.success('Fetched successfully.', 'Success', {
            position: 'topRight',
          })
        } else {
          this.$toast.error(tokenRes.data.message, 'Error', {
            position: 'topRight',
          })
        }
      },

      async saveTelnetHostsDetails (editedItem, { item }) {
        const hosts = editedItem?.hosts || []

        if (this.selectedDevices.findIndex(x => x.id === item.id) === -1) {
          this.selectedDevices.push(item)
        }

        const deviceIds = this.selectedDevices?.map(x => x.id) || []

        const res = await this.$http.put('/DeviceSecurityDetails/update', { hosts, deviceIds, expiry: editedItem.expiry, active: editedItem.active })
        if (res.data.success) {
          const length = hosts.length
          if (length > 0) {
            const addedHosts = res?.data?.data || []

            hosts.forEach(host => {
              const index = editedItem?.hosts?.findIndex(x => x.serverName === host.serverName) || -1
              if (index > -1) {
                editedItem.hosts.splice(index, 1)
              }
            })

            this.selectedDevices.forEach((device, index) => {
              device.telnetHosts.length = 0
              const items = addedHosts.slice(index * length, (index + 1) * length)
              device.telnetHosts.push(...items)
            })
          }

          this.selectedDevices.forEach(device => {
            device.telnetExpiry = editedItem.expiry
            device.active = editedItem.active
          })

          this.$toast.success('Saved successfully.', 'Success', {
            position: 'topRight',
          })
        } else {
          this.$toast.error(res.data.message, 'Error', {
            position: 'topRight',
          })
        }
      },

      // async updateTelnetHostsDetails (editedItem, form, { item }) {
      //   const response = await this.$http.put(`/DeviceSecurityDetails/${editedItem.id}`, editedItem)
      //   if (response.data.success) {
      //     const formIndex = form.hosts.findIndex(x => x.id === editedItem.id)
      //     form.hosts.splice(formIndex, 1, response.data.data)

      //     const index = item.telnetHosts.findIndex(x => x.id === editedItem.id)
      //     item.telnetHosts.splice(index, 1, response.data.data)

      //     this.$toast.success('Updated successfully.', 'Success', {
      //       position: 'topRight',
      //     })
      //   } else {
      //     this.$toast.error(response.data.message, 'Error', {
      //       position: 'topRight',
      //     })
      //   }
      // },

      // async updateTelnetServerDetails (editedItem, { item }) {
      //   const response = await this.$http.post(`/DeviceSecurityDetails/${item.id}/AddOrUpdate`, editedItem)
      //   if (!response.data.success) {
      //     this.$toast.error(response.data.message, 'Error', {
      //       position: 'topRight',
      //     })
      //     return
      //   }

      //   const tokenRes = await this.$http(`/DevicesQRAuthentication/generate?deviceId=${item.id}`)
      //   if (tokenRes.data.success) {
      //     item.telnet = response.data.data
      //     item.telnet.token = tokenRes.data.data.token

      //     this.$toast.success('Update successfully.', 'Success', {
      //       position: 'topRight',
      //     })
      //   } else {
      //     this.$toast.error(tokenRes.data.message, 'Error', {
      //       position: 'topRight',
      //     })
      //   }
      // },
      // async showTelnetServerDetails (item) {
      //   if (!item.telnet) {
      //     const res = await this.$http(`/DeviceSecurityDetails/${item.id}/GetDeviceSecurityDetail`)
      //     item.telnet = res.data?.data || {}
      //   }

      //   this.dialogTelnetServerDetails.show(item.telnet, { formTitle: 'Telnet Server Details', item })
      // },
    },
  }
</script>
<style scoped>
.devices-toolbar-row {
  width: 100%;
}

.devices-new-switch {
  max-width: 100%;
}

.devices-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
}

@media (min-width: 600px) {
  .devices-toolbar-actions {
    justify-content: flex-end;
  }
}

.devices-toolbar-actions-menu {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.devices-search-field {
  max-width: 100%;
}

@media (min-width: 600px) {
  .devices-search-field {
    max-width: 280px;
  }
}

.devices-table-scroll {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ---- Custom mobile device cards -------------------------------------- */
.device-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 8px;
}

.device-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 10px 12px;
}

/* Checkbox + image + name + expand all on a single compact header row. */
.device-card__top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.device-card__check {
  flex: 0 0 auto;
}

.device-card__icon {
  flex: 0 0 auto;
  width: 72px;
  display: flex;
  justify-content: center;
}

.device-card__icon ::v-deep svg,
.device-card__icon ::v-deep img {
  width: 64px;
  height: auto;
}

/* DeviceIcon renders its own model-name label; hide it here so it doesn't
   overlap the heading (the model is shown once under the name instead). */
.device-card__icon ::v-deep span {
  display: none;
}

.device-card__heading {
  flex: 1 1 auto;
  min-width: 0;
}

.device-card__name {
  display: block;
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-card__model {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-card__expand {
  flex: 0 0 auto;
}

.device-card__top {
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.device-card__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.device-card__meta {
  margin-top: 4px;
}

.device-card__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.85rem;
  padding: 7px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.device-card__meta-row:last-child {
  border-bottom: none;
}

.device-card__meta-row--stacked {
  display: grid;
  grid-template-columns: 1fr;
  justify-content: stretch;
}

.app-version-table {
  font-size: 0.875rem;
  line-height: 1.43;
  border-collapse: collapse;
  white-space: nowrap;
}

.app-version-table th {
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #f9a825;
  padding: 4px 6px 1px 0;
}

.app-version-table td {
  padding: 0 6px 0 0;
  color: rgba(0, 0, 0, 0.6);
}

.app-version-table__value--mydevice {
  color: rgba(0, 0, 0, 0.8);
  font-weight: 600;
}

.app-version-table__value--default {
  color: #2e7d32;
  font-weight: 600;
}

.app-version-table__value--taxi {
  color: #ef6c00;
  font-weight: 600;
}

.device-card__k {
  color: rgba(0, 0, 0, 0.55);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.device-card__v {
  text-align: right;
  word-break: break-word;
}

.device-card__info-button {
  max-width: none;
}

.device-card__expanded {
  margin-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 8px;
}

.device-cards__empty {
  text-align: center;
  color: rgba(0, 0, 0, 0.5);
  padding: 24px 0;
}

.device-cards__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 0 4px;
}

.device-cards__page-label {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}

/* Real-table mode (>= mobile breakpoint) can scroll horizontally; in stacked
   mobile mode the cards must fit the screen, so never force a min width there. */
.devices-data-table {
  min-width: 0;
}

@media (min-width: 960px) {
  .devices-table-scroll .devices-data-table {
    min-width: 760px;
  }
}

.devices-network {
  width: 100%;
  max-width: 212px;
  font-size: 0.875rem;
  line-height: 1.43;
  text-align: left;
}

/* One shared grid for every row so the labels line up while the value column
   keeps whatever width is left over instead of a fixed gutter. */
.devices-network__grid {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 6px;
  row-gap: 4px;
  align-items: start;
}

.devices-network__cell {
  display: contents;
}

.devices-network__k {
  display: block;
  width: auto;
  flex-shrink: 0;
  font-weight: 700;
  color: #2e7d32;
  font-size: 0.75rem;
  line-height: 1.2;
  min-height: auto;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding-top: 2px;
  white-space: nowrap;
}

.devices-network__k::after {
  content: ':';
  margin-left: 4px;
}

.theme--dark .devices-network__k {
  color: #81c784;
}

.devices-network__v {
  display: block;
  width: 100%;
  min-width: 0;
  font-size: inherit;
  line-height: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.devices-network__v--essid {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.devices-data-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  text-align: left;
}

.devices-data-link {
  display: block;
  width: 100%;
  max-width: 220px;
  padding: 8px 4px 8px 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
  line-height: 1.3;
  color: inherit;
  min-height: 40px;
  box-sizing: border-box;
}

.devices-data-link:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.devices-info-cell {
  display: block;
  width: 100%;
  max-width: 212px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.devices-info-cell--button:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.devices-info-grid {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 6px;
  row-gap: 4px;
  align-items: start;
  font-size: 0.875rem;
  line-height: 1.43;
}

.devices-info-grid__row {
  display: contents;
}

.devices-info-grid__k {
  display: block;
  min-width: 0;
  /* Caps the shared label track so an unusually long meta label cannot
     starve the value column. */
  max-width: 96px;
  padding-top: 2px;
  color: #2e7d32;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.devices-info-grid__k::after {
  content: ':';
  margin-left: 4px;
}

.theme--dark .devices-info-grid__k {
  color: #81c784;
}

.devices-info-grid__v {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.devices-info-empty {
  text-decoration: underline;
}

.devices-row-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 60px;
  margin: 0 auto;
  align-items: stretch;
  padding: 2px 0;
}

.devices-row-actions__pair {
  display: grid;
  grid-template-columns: repeat(2, 28px);
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.devices-action-btn {
  flex: 0 0 auto;
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
}

.unlock-dialog-title {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}

.unlock-code {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  background: #263238;
  color: #fff;
}

.unlock-code--compact {
  display: inline-flex;
  min-width: 116px;
  padding: 6px 10px;
  justify-content: center;
}

.unlock-code__label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.unlock-code__value {
  font-family: monospace;
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.unlock-code--compact .unlock-code__value {
  font-size: 0.95rem;
  letter-spacing: 0.06em;
}

.unlock-company-section {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding-top: 14px;
  margin-top: 14px;
}

.unlock-company-section:first-of-type {
  border-top: 0;
  padding-top: 0;
  margin-top: 0;
}

.unlock-company-section__title {
  display: flex;
  align-items: center;
  min-width: 0;
  margin-bottom: 8px;
  font-weight: 700;
}

.unlock-company-section__table {
  overflow-x: auto;
}

.unlock-device-status {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  font-size: 0.75rem;
  color: #455a64;
}

.unlock-row-actions {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
}

.devices-taxi-link {
  display: block;
  width: fit-content;
  margin-top: 4px;
  text-decoration: none;
}

.devices-name-action {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
  font: inherit;
}

.device-additional-info-grid {
  display: grid;
  gap: 10px;
}

.device-additional-info-row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.device-additional-info-row__label {
  font-weight: 700;
  color: #455a64;
}

.device-additional-info-row__value {
  word-break: break-word;
}

.device-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.device-meta-row__label {
  flex: 0 0 38%;
}

.device-meta-row__value {
  flex: 1 1 auto;
}

.device-meta-row__remove {
  flex: 0 0 auto;
}

.force-uninstall-devices {
  display: grid;
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;
  margin-top: 10px;
}

.force-uninstall-device {
  padding: 6px 8px;
  border-radius: 4px;
  background: #f5f5f5;
  font-size: 0.875rem;
}

@media (max-width: 599px) {
  .unlock-code {
    flex-direction: column;
    align-items: flex-start;
  }

  .device-meta-row {
    flex-wrap: wrap;
  }

  .device-meta-row__label,
  .device-meta-row__value {
    flex: 1 1 100%;
  }

  .device-additional-info-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .unlock-row-actions {
    justify-content: flex-start;
  }

  .unlock-row-actions .v-btn {
    width: 100%;
  }
}
 </style>
<style>
.devices-actions-cell {
  width: 84px !important;
  min-width: 84px !important;
  max-width: 84px !important;
  overflow: visible !important;
  vertical-align: middle !important;
  text-align: center !important;
  white-space: nowrap !important;
}
</style>
